import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "./TalentPulse360Survey.css";

// components
import Modal from "../../components/Modal/Modal";
import DropdownSelector from "../../components/DropdownSelector/DropdownSelector";
import DateTimePicker from "../../components/DateTimePicker";
import MasterTable from "../../components/MasterTable/MasterTable";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import useOutsideClick from "../../hooks/useOutsideClick";

// assets
import closeIcon from "../../assets/new-images/close-icon.svg";
import deleteIcon from "../../assets/new-images/delete-icon.svg";

// api's and constants
import { useConstants } from "../../constant/constantProvider";
import {
  fetchTalentPulseSurveys,
  fetchTalentPulseSurveySends,
  createTalentPulseSurvey,
  fetchSurveyUsers,
  sendTalentPulseSurvey,
  analyseTalentPulseSurvey,
  fetchTalentPulseSurveyStatus,
  fetchTalentPulseSurveyDashboard,
  fetchTalentPulseSurveyCardsData,
} from "../../store/apiActions";
import { formatDateTimeLocal, formatCreatedDate } from "../../utils/dateUtils";
import {
  QUESTION_TYPE_OPTIONS,
  normalizeQuestionType,
  mapQuestionsToPayload,
  mapSurveyToCard,
  getSurveyRows,
  getUserRows,
  normalizeActionResult,
} from "../../utils/talentPulseUtils";

const MIN_SURVEY_DURATION_MS = 60 * 60 * 1000;
const getMinimumEndDateTime = (startDateTime) => {
  if (!startDateTime) return null;
  return new Date(new Date(startDateTime).getTime() + MIN_SURVEY_DURATION_MS);
};

const CARD_ICONS = { lightning: "⚡", "user-group": "👥", smile: "😊" };

const CloseButton = ({ onClick, closeLabel }) => (
  <button type="button" className="talent-survey-modal-close-btn" onClick={onClick} aria-label={closeLabel}>
    <img src={closeIcon} alt="" />
  </button>
);


const INITIAL_SCHEDULED = [
  {
    id: "sched-1",
    surveyTitle: "Pulse Trigger",
    surveySub: "Monthly · anonymous",
    recipientGroup: "Maharashtra TSM cohort",
    recipientCount: "42 people",
    opensDate: "15 Aug, 09:00",
    opensSub: "IST · in 2 days",
    closesDate: "22 Aug, 18:00",
    closesSub: "7-day window",
    flowStatus: "Queued",
    flowSub: "run #8841",
    isLocked: false
  },
  {
    id: "sched-2",
    surveyTitle: "Moodometer",
    surveySub: "Ad hoc",
    recipientGroup: "West zone field team",
    recipientCount: "28 people",
    opensDate: "18 Aug, 10:00",
    opensSub: "IST · in 5 days",
    closesDate: "25 Aug, 23:59",
    closesSub: "7-day window",
    flowStatus: "Queued",
    flowSub: "run #8846",
    isLocked: false
  },
  {
    id: "sched-3",
    surveyTitle: "Manager Feedback",
    surveySub: "Anonymous · min 5 replies",
    recipientGroup: "Reports of R. Mehta",
    recipientCount: "9 people",
    opensDate: "20 Aug, 09:30",
    opensSub: "IST · in 7 days",
    closesDate: "03 Sep, 18:00",
    closesSub: "14-day window",
    flowStatus: "Approval pending",
    flowSub: "HRBP sign-off",
    isLocked: false
  },
  {
    id: "sched-4",
    surveyTitle: "Pulse Trigger",
    surveySub: "Triggered on day 30",
    recipientGroup: "Aug joiners",
    recipientCount: "6 people",
    opensDate: "Rolling",
    opensSub: "per start date",
    closesDate: "+5 days each",
    closesSub: "auto-close",
    flowStatus: "Listening",
    flowSub: "trigger armed",
    isLocked: false
  },
  {
    id: "sched-5",
    surveyTitle: "Moodometer",
    surveySub: "Weekly · anonymous",
    recipientGroup: "All field staff",
    recipientCount: "180 people",
    opensDate: "Today, 09:00",
    opensSub: "started 4 min ago",
    closesDate: "15 Aug, 18:00",
    closesSub: "7-day window",
    flowStatus: "Sending",
    flowSub: "112 of 180 sent",
    isLocked: true
  }
];

// Mock in-progress surveys data
const INITIAL_IN_PROGRESS = [
  {
    id: "prog-1",
    surveyTitle: "Moodometer",
    surveySub: "Opened 11 Aug, 09:00",
    audienceGroup: "All field staff",
    audienceCount: "180 sent",
    responsesStr: "141 of 180",
    responsesPct: "78%",
    responsesNum: 78,
    progressColor: "green",
    closesText: "2 days",
    closesSub: "15 Aug, 18:00",
    reminderText: "Due in 4h",
    reminderSub: "39 pending",
    reminderStatus: "warning",
    deliveryText: "Delivered",
    deliverySub: "180 sent · 0 failed",
    deliveryStatus: "success",
    deliveryPct: 100,
    deliveryColor: "green",
    actionType: "view"
  },
  {
    id: "prog-2",
    surveyTitle: "Manager Feedback — R. Mehta",
    surveySub: "Opened 09 Aug, 10:00",
    audienceGroup: "Peers & reports",
    audienceCount: "11 sent",
    responsesStr: "7 of 11",
    responsesPct: "64%",
    responsesNum: 64,
    progressColor: "blue",
    closesText: "4 days",
    closesSub: "17 Aug, 18:00",
    reminderText: "Due 15 Aug",
    reminderSub: "4 pending",
    reminderStatus: "warning",
    deliveryText: "Delivered",
    deliverySub: "11 sent · 0 failed",
    deliveryStatus: "success",
    deliveryPct: 100,
    deliveryColor: "green",
    actionType: "view"
  },
  {
    id: "prog-3",
    surveyTitle: "Pulse Trigger",
    surveySub: "Opened 05 Aug, 09:00",
    audienceGroup: "Gujarat TSM cohort",
    audienceCount: "36 sent",
    responsesStr: "13 of 36",
    responsesPct: "36%",
    responsesNum: 36,
    progressColor: "orange",
    closesText: "Today",
    closesSub: "13 Aug, 18:00",
    reminderText: "Sent 11 Aug",
    reminderSub: "23 pending",
    reminderStatus: "success",
    deliveryText: "Partially failed",
    deliverySub: "33 sent · 3 failed",
    deliveryStatus: "danger",
    deliveryPct: 91,
    deliveryColor: "mixed",
    actionType: "retry",
    actionText: "Retry 3"
  }
];

const inProgressColumns = [
  {
    key: "survey",
    label: "SURVEY",
    render: (item) => (
      <div className="table-col-stacked">
        <span className="table-col-primary">{item.surveyTitle}</span>
        <span className="table-col-secondary">{item.surveySub}</span>
      </div>
    )
  },
  {
    key: "audience",
    label: "AUDIENCE",
    render: (item) => (
      <div className="table-col-stacked">
        <span className="table-col-primary table-col-primary-regular">{item.audienceGroup}</span>
        <span className="table-col-secondary">{item.audienceCount}</span>
      </div>
    )
  },

  {
    key: "closes",
    label: "CLOSES IN",
    render: (item) => (
      <div className="table-col-stacked">
        <span className="table-col-primary table-col-primary-regular">{item.closesText}</span>
        <span className="table-col-secondary">{item.closesSub}</span>
      </div>
    )
  },
  {
    key: "reminder",
    label: "REMINDER",
    render: (item) => (
      <div className="table-col-stacked">
        <span className={`table-flow-pill pill-${item.reminderStatus}`}>{item.reminderText}</span>
        <span className="table-col-secondary mt-1">{item.reminderSub}</span>
      </div>
    )
  },
  {
    key: "delivery",
    label: "DELIVERY",
    render: (item) => (
      <div className="table-col-stacked delivery-column">
        <span className={`table-flow-pill pill-${item.deliveryStatus}`}>{item.deliveryText}</span>
        <div className="delivery-bar-row mt-1">
          {item.deliveryColor === "mixed" ? (
            <div className="progress-bar-bg progress-bar-mixed">
              <div className={`progress-bar-fill fill-green progress-width-${item.deliveryPct}`} />
              <div className={`progress-bar-fill fill-red progress-width-${100 - item.deliveryPct}`} />
            </div>
          ) : (
            <div className="progress-bar-bg">
              <div className={`progress-bar-fill fill-${item.deliveryColor} progress-width-${item.deliveryPct}`} />
            </div>
          )}
          <span className="table-col-secondary delivery-detail">{item.deliverySub}</span>
        </div>
      </div>
    )
  },
];




const TalentPulse360Survey = () => {
  const dispatch = useDispatch();
  const { talentPulse360, NO_DATA_FOUND } = useConstants();
  const noDataText = NO_DATA_FOUND || "No data found";
  const [activeTab, setActiveTab] = useState("forms"); // "forms" | "scheduled" | "in_progress" | "analysis"
  const [surveyForms, setSurveyForms] = useState([]);
  const [scheduledList, setScheduledList] = useState(INITIAL_SCHEDULED);
  const [inProgressList, setInProgressList] = useState(INITIAL_IN_PROGRESS);
  const [analysisSurveys, setAnalysisSurveys] = useState([]);

  const [surveysLoading, setSurveysLoading] = useState(false);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [inProgressLoading, setInProgressLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  // Load published survey forms when the forms tab is opened.
  useEffect(() => {
    if (activeTab === "forms") {
      setSurveysLoading(true);
      dispatch(fetchTalentPulseSurveys({ status: "published", page: 1, page_size: 10 }))
        .then(normalizeActionResult).then(({ data, error }) => {
          if (error) {
            toast.error(error.message || "Unable to load surveys.");
            return;
          }
          setSurveyForms(getSurveyRows(data).map(mapSurveyToCard).filter((survey) => survey.id));
        }).finally(() => setSurveysLoading(false));
    } else if (activeTab === "scheduled") {
      setScheduledLoading(true);
      const timer = setTimeout(() => setScheduledLoading(false), 750);
      return () => clearTimeout(timer);
    } else if (activeTab === "in_progress") {
      setInProgressLoading(true);
      const timer = setTimeout(() => setInProgressLoading(false), 750);
      return () => clearTimeout(timer);
    } else if (activeTab === "analysis") {
      setAnalysisLoading(true);
      dispatch(fetchTalentPulseSurveySends({ page: 1, page_size: 10 }))
        .then(normalizeActionResult).then(({ data, error }) => {
          if (error) {
            toast.error(error.message || "Unable to load surveys for analysis.");
            return;
          }
          setAnalysisSurveys(getSurveyRows(data).filter((survey) => survey.survey_send_id || survey.id || survey.survey_id || survey.send_id));
        }).finally(() => setAnalysisLoading(false));
    }
  }, [activeTab, dispatch]);

  // Pre-fetch analysis surveys on mount so the tab badge count shows immediately
  useEffect(() => {
    dispatch(fetchTalentPulseSurveySends({ page: 1, page_size: 10 }))
      .then(normalizeActionResult).then(({ data, error }) => {
        if (error) return;
        setAnalysisSurveys(getSurveyRows(data).filter((survey) => survey.survey_send_id || survey.id || survey.survey_id || survey.send_id));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedAnalysisSurvey, setSelectedAnalysisSurvey] = useState(null);
  const [selectedAnalysisWave, setSelectedAnalysisWave] = useState(null);
  const [selectedAnalysisWaveId, setSelectedAnalysisWaveId] = useState(null);
  const [themePanelOpen, setThemePanelOpen] = useState(false);
  const [themePanelTab, setThemePanelTab] = useState("insight");
  const [analysisPanelType, setAnalysisPanelType] = useState("themes");

  // Survey Analysis trigger & polling state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingWaveId, setAnalyzingWaveId] = useState(null);
  const [isAnalysisReady, setIsAnalysisReady] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const analysisPollingRef = useRef(null);

  // Survey Dashboard cards data state
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // Survey individual card details data state (themes, sentiment, ratings)
  const [cardsDetailData, setCardsDetailData] = useState({});
  const [cardDetailLoading, setCardDetailLoading] = useState(false);

  const resetAnalysisState = () => {
    setSelectedAnalysisSurvey(null);
    setSelectedAnalysisWave(null);
    setSelectedAnalysisWaveId(null);
    setThemePanelOpen(false);
    setThemePanelTab("insight");
    setAnalysisPanelType("themes");
    setDashboardData(null);
    setCardsDetailData({});
    setIsAnalysisReady(false);
    setIsAnalyzing(false);
    setAnalyzingWaveId(null);
    setIsCheckingStatus(false);
    if (analysisPollingRef.current) {
      clearInterval(analysisPollingRef.current);
      analysisPollingRef.current = null;
    }
  };

  const handleBackToSurveyWaves = () => {
    setSelectedAnalysisWave(null);
    setSelectedAnalysisWaveId(null);
    setThemePanelOpen(false);
    setThemePanelTab("insight");
    setAnalysisPanelType("themes");
    setDashboardData(null);
    setCardsDetailData({});
  };

  const handleFetchDashboard = useCallback(async (surveyId) => {
    if (!surveyId) return;

    setDashboardLoading(true);
    try {
      const res = await dispatch(fetchTalentPulseSurveyDashboard(surveyId));
      const { data, error } = normalizeActionResult(res);
      if (error) {
        console.warn("Failed to fetch dashboard data:", error);
      } else if (data) {
        const payload = data?.data || data?.result || data;
        setDashboardData(payload);
      }
    } catch (err) {
      console.error("Error fetching talentPulseSurveyDashboard:", err);
    } finally {
      setDashboardLoading(false);
    }
  }, [dispatch]);

  const fetchCardDetail = useCallback(
    async (cardType, surveyId) => {
      if (!cardType || !surveyId) return;

      setCardDetailLoading(true);
      try {
        const res = await dispatch(fetchTalentPulseSurveyCardsData(surveyId, cardType));
        const { data, error } = normalizeActionResult(res);
        if (error) {
          console.warn(`Failed to fetch card details for ${cardType}:`, error);
        } else if (data) {
          const payload = data?.data || data?.result || data;
          setCardsDetailData((prev) => ({
            ...prev,
            [cardType]: payload,
          }));
        }
      } catch (err) {
        console.error(`Error fetching talentPulseSurveyCardsData for ${cardType}:`, err);
      } finally {
        setCardDetailLoading(false);
      }
    },
    [dispatch]
  );

  const handleOpenAnalysisCard = (cardType) => {
    if (themePanelOpen && analysisPanelType === cardType) {
      setThemePanelOpen(false);
    } else {
      setAnalysisPanelType(cardType);
      setThemePanelOpen(true);
      setThemePanelTab("insight");
      if (cardType === "themes" || cardType === "sentiment" || cardType === "ratings") {
        const cardSurveyId =
          selectedAnalysisWave?.survey_send_id ||
          selectedAnalysisWave?.send_id ||
          selectedAnalysisWave?.id ||
          selectedAnalysisSurvey?.survey_send_id ||
          selectedAnalysisSurvey?.send_id ||
          selectedAnalysisSurvey?.id;
        fetchCardDetail(cardType, cardSurveyId);
      }
    }
  };
  useEffect(() => {
    return () => {
      if (analysisPollingRef.current) {
        clearInterval(analysisPollingRef.current);
        analysisPollingRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "analysis") {
      resetAnalysisState();
      if (analysisPollingRef.current) {
        clearInterval(analysisPollingRef.current);
        analysisPollingRef.current = null;
      }
      setIsAnalyzing(false);
      setAnalyzingWaveId(null);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "analysis" && selectedAnalysisWave) {
      const targetSurveyId =
        selectedAnalysisWave?.survey_send_id ||
        selectedAnalysisWave?.send_id ||
        selectedAnalysisWave?.id ||
        selectedAnalysisSurvey?.survey_send_id ||
        selectedAnalysisSurvey?.send_id ||
        selectedAnalysisSurvey?.id;
      handleFetchDashboard(targetSurveyId);
    }
  }, [activeTab, selectedAnalysisWave, selectedAnalysisSurvey, handleFetchDashboard]);

  const startStatusPolling = useCallback(
    (surveySendId, waveId = null) => {
      if (analysisPollingRef.current) {
        clearInterval(analysisPollingRef.current);
        analysisPollingRef.current = null;
      }

      analysisPollingRef.current = setInterval(async () => {
        try {
          const statusRes = await dispatch(fetchTalentPulseSurveyStatus(surveySendId));

          if (statusRes?.error) {
            console.warn("Error polling survey status:", statusRes.error);
            return;
          }

          const ready = Boolean(
            statusRes?.ready === true ||
            statusRes?.data?.ready === true ||
            statusRes?.result?.ready === true ||
            statusRes?.is_ready === true ||
            statusRes?.data?.is_ready === true ||
            statusRes?.status === "ready" ||
            statusRes?.status === "completed" ||
            statusRes?.data?.status === "ready" ||
            statusRes?.data?.status === "completed"
          );

          if (ready) {
            if (analysisPollingRef.current) {
              clearInterval(analysisPollingRef.current);
              analysisPollingRef.current = null;
            }
            setIsAnalysisReady(true);
            setIsAnalyzing(false);
            setAnalyzingWaveId(null);
            handleFetchDashboard(surveySendId);
            return;
          }

          // Handle terminal failure statuses
          if (
            statusRes?.status === "failed" ||
            statusRes?.status === "timed_out" ||
            statusRes?.suppressed ||
            statusRes?.data?.status === "failed"
          ) {
            if (analysisPollingRef.current) {
              clearInterval(analysisPollingRef.current);
              analysisPollingRef.current = null;
            }
            setIsAnalyzing(false);
            setAnalyzingWaveId(null);
            toast.error(statusRes?.message || statusRes?.data?.message || "Analysis failed");
          }
        } catch (pollErr) {
          console.error("Error in status polling:", pollErr);
        }
      }, 3000);
    },
    [dispatch, handleFetchDashboard]
  );

  const checkSurveyAnalysisStatus = useCallback(
    async (survey, wave) => {
      const apiSurveySendId =
        wave?.survey_send_id ||
        wave?.send_id ||
        survey?.survey_send_id ||
        survey?.send_id ||
        wave?.id ||
        survey?.id ||
        selectedAnalysisSurvey?.survey_send_id ||
        selectedAnalysisSurvey?.send_id ||
        selectedAnalysisSurvey?.id;
      if (!apiSurveySendId) return;

      setIsCheckingStatus(true);
      try {
        const statusRes = await dispatch(fetchTalentPulseSurveyStatus(apiSurveySendId));
        if (statusRes?.error) {
          console.warn("Error checking survey analysis status:", statusRes.error);
          setIsAnalysisReady(false);
          return;
        }

        const isReady = Boolean(
          statusRes?.ready === true ||
          statusRes?.data?.ready === true ||
          statusRes?.result?.ready === true ||
          statusRes?.is_ready === true ||
          statusRes?.data?.is_ready === true ||
          statusRes?.status === "ready" ||
          statusRes?.status === "completed" ||
          statusRes?.data?.status === "ready" ||
          statusRes?.data?.status === "completed"
        );

        const isRunning = Boolean(
          statusRes?.status === "running" ||
          statusRes?.status === "in_progress" ||
          statusRes?.status === "processing" ||
          statusRes?.data?.status === "running" ||
          statusRes?.data?.status === "in_progress"
        );

        if (isReady) {
          setIsAnalysisReady(true);
          setIsAnalyzing(false);
          setAnalyzingWaveId(null);
        } else if (isRunning) {
          setIsAnalysisReady(false);
          setIsAnalyzing(true);
          setAnalyzingWaveId(wave?.id || survey?.latest?.id || survey?.waves?.[0]?.id || null);
          startStatusPolling(apiSurveySendId, wave?.id);
        } else {
          setIsAnalysisReady(false);
        }
      } catch (err) {
        console.error("Failed to check analysis status:", err);
        setIsAnalysisReady(false);
      } finally {
        setIsCheckingStatus(false);
      }
    },
    [dispatch, startStatusPolling, selectedAnalysisSurvey]
  );

  useEffect(() => {
    if (activeTab === "analysis" && selectedAnalysisSurvey && !selectedAnalysisWave && !isAnalysisReady && !isAnalyzing) {
      const primaryWave = selectedAnalysisSurvey.latest || selectedAnalysisSurvey.waves?.[0] || {};
      checkSurveyAnalysisStatus(selectedAnalysisSurvey, primaryWave);
    }
  }, [activeTab, selectedAnalysisSurvey, selectedAnalysisWave, isAnalysisReady, isAnalyzing, checkSurveyAnalysisStatus]);

  const handleRunAnalysis = async (wave) => {
    if (isAnalyzing || isCheckingStatus) return;

    if (analysisPollingRef.current) {
      clearInterval(analysisPollingRef.current);
      analysisPollingRef.current = null;
    }

    const apiSurveySendId =
      wave?.survey_send_id ||
      wave?.send_id ||
      selectedAnalysisSurvey?.survey_send_id ||
      selectedAnalysisSurvey?.send_id ||
      wave?.id ||
      selectedAnalysisSurvey?.id;
    if (!apiSurveySendId) {
      toast.error("No survey send ID found. Please select a survey first.");
      return;
    }

    setIsAnalyzing(true);
    setAnalyzingWaveId(wave?.id || null);
    toast.info("Analysing, please wait...");

    try {
      // 1. Hit API talentPulseSurveyAnalyse with survey send id
      const analyseRes = await dispatch(analyseTalentPulseSurvey(apiSurveySendId));

      if (analyseRes?.error) {
        toast.error(analyseRes.error.message || "Failed to trigger analysis");
        setIsAnalyzing(false);
        setAnalyzingWaveId(null);
        return;
      }

      // Check if analyse endpoint immediately returns ready === true
      const immediatelyReady = Boolean(
        analyseRes?.ready === true ||
        analyseRes?.data?.ready === true ||
        analyseRes?.result?.ready === true ||
        analyseRes?.is_ready === true ||
        analyseRes?.data?.is_ready === true ||
        analyseRes?.status === "ready" ||
        analyseRes?.status === "completed"
      );

      if (immediatelyReady) {
        setIsAnalysisReady(true);
        setIsAnalyzing(false);
        setAnalyzingWaveId(null);
        handleFetchDashboard(apiSurveySendId);
        return;
      }

      // 2. Poll talentPulseSurveyStatus every 3 seconds (3000ms)
      startStatusPolling(apiSurveySendId, wave?.id);
    } catch (err) {
      console.error("Error calling talentPulseSurveyAnalyse:", err);
      if (analysisPollingRef.current) {
        clearInterval(analysisPollingRef.current);
        analysisPollingRef.current = null;
      }
      setIsAnalyzing(false);
      setAnalyzingWaveId(null);
      toast.error("Failed to run analysis");
    }
  };

  const analysisRows = useMemo(() => analysisSurveys.map((survey) => {
    const createdAt = survey.created_at || survey.created_date || survey.creation_date || survey.sent_at || survey.start_time || "";
    const status = survey.status || "published";
    const surveyId = survey.survey_id || survey.id;
    const sendId = survey.survey_send_id || survey.send_id || survey.id || survey.survey_id;
    const surveySendId = survey.survey_send_id || survey.send_id || sendId;
    const title = survey.survey_name || survey.name || survey.title || survey.survey_title || "Untitled survey";
    const sub = survey.survey_description || survey.description || survey.survey_sub || survey.audience || "";
    return {
      ...survey,
      id: sendId,
      surveyId,
      survey_id: surveyId,
      survey_send_id: surveySendId,
      send_id: surveySendId,
      survey_name: title,
      survey_description: sub,
      name: title,
      description: sub,
      surveyTitle: title,
      surveySub: sub,
      status,
      created_at: createdAt,
      created_date: createdAt,
      // keep wave-shape for the drill-down card (primary wave = the survey itself)
      latest: {
        ...survey,
        id: sendId,
        survey_id: surveyId,
        survey_send_id: surveySendId,
        send_id: surveySendId,
        survey_name: title,
        survey_description: sub,
        name: title,
        description: sub,
        surveyTitle: title,
        surveySub: sub,
        status,
        created_at: createdAt,
        created_date: createdAt,
      },
      waves: [{
        ...survey,
        id: sendId,
        survey_id: surveyId,
        survey_send_id: surveySendId,
        send_id: surveySendId,
        survey_name: title,
        survey_description: sub,
        name: title,
        description: sub,
        surveyTitle: title,
        surveySub: sub,
        status,
        created_at: createdAt,
        created_date: createdAt,
      }],
    };
  }), [analysisSurveys]);

  const analysisColumns = [
    {
      key: "survey",
      label: "SURVEY",
      render: (item) => (
        <div className="table-col-stacked">
          <span className="table-col-primary">{item.surveyTitle}</span>
          {item.surveySub && <span className="table-col-secondary">{item.surveySub}</span>}
        </div>
      ),
    },
    {
      key: "waves",
      label: "WAVES",
      render: () => (
        <div className="analysis-table-wave-cell">
          <strong>1</strong>
          <span>completed wave</span>
        </div>
      ),
    },

  ];

  // Modals state
  const [previewSurvey, setPreviewSurvey] = useState(null);
  const [sendModalSurvey, setSendModalSurvey] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteConfirmSurvey, setDeleteConfirmSurvey] = useState(null);
  const [isDeletingSurvey, setIsDeletingSurvey] = useState(false);

  // Edit Scheduled Send Modal state
  const [editScheduledSend, setEditScheduledSend] = useState(null);
  const [editRecipients, setEditRecipients] = useState([]);
  const [isEditRecipientDropdownOpen, setIsEditRecipientDropdownOpen] = useState(false);
  const [editStartDateTime, setEditStartDateTime] = useState("2026-08-15T09:00");
  const [editEndDateTime, setEditEndDateTime] = useState("2026-08-22T18:00");
  const editSendToBoxRef = useRef(null);
  // Cancel Scheduled Send Confirmation Modal state
  const [cancelConfirmItem, setCancelConfirmItem] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const handleOpenEditScheduledModal = (item) => {
    setEditScheduledSend(item);
    setIsEditRecipientDropdownOpen(false);
    setEditRecipients([
      { id: "opt-1", type: "COHO", text: `${item.recipientGroup} · ${item.recipientCount ? item.recipientCount.replace(' people', '') : '42'}`, count: parseInt(item.recipientCount) || 42 }
    ]);
    const startDateTime = "2026-08-15T09:00";
    setEditStartDateTime(startDateTime);
    setEditEndDateTime(formatDateTimeLocal(getMinimumEndDateTime(startDateTime)));
  };

  const handleSaveEditScheduledSend = () => {
    if (!editScheduledSend) return;
    if (!editStartDateTime || !editEndDateTime || new Date(editEndDateTime) < getMinimumEndDateTime(editStartDateTime)) {
      toast.error("End date and time must be at least 1 hour after the start date and time.");
      return;
    }
    const recipientSummary = editRecipients.map(r => r.text.split('·')[0].trim()).join(", ") || editScheduledSend.recipientGroup;
    const totalCount = editRecipients.reduce((acc, item) => acc + (item.count || 1), 0);

    setScheduledList(prev => prev.map(item => {
      if (item.id === editScheduledSend.id) {
        return {
          ...item,
          recipientGroup: recipientSummary,
          recipientCount: `${totalCount} people`,
        };
      }
      return item;
    }));
    toast.success(talentPulse360.SCHEDULED_UPDATE_SUCCESS);
    setEditScheduledSend(null);
  };

  const handleCancelScheduledSend = (item) => {
    setCancelConfirmItem(item);
    setCancelReason("");
  };

  const handleConfirmCancelScheduledSend = () => {
    if (!cancelConfirmItem) return;
    setScheduledList(prev => prev.filter(s => s.id !== cancelConfirmItem.id));
    toast.info(talentPulse360.SCHEDULED_CANCEL_SUCCESS);
    setCancelConfirmItem(null);
    setCancelReason("");
  };

  const scheduledColumns = useMemo(() => [
    {
      key: "survey",
      label: "SURVEY",
      render: (item) => (
        <div className="table-col-stacked">
          <span className="table-col-primary">{item.surveyTitle}</span>
          <span className="table-col-secondary">{item.surveySub}</span>
        </div>
      )
    },
    {
      key: "recipients",
      label: "RECIPIENTS",
      render: (item) => (
        <div className="table-col-stacked">
          <span className="table-col-primary">{item.recipientGroup}</span>
          <span className="table-col-secondary">{item.recipientCount}</span>
        </div>
      )
    },
    {
      key: "opens",
      label: "OPENS",
      render: (item) => (
        <div className="table-col-stacked">
          <span className="table-col-primary">{item.opensDate}</span>
          <span className="table-col-secondary">{item.opensSub}</span>
        </div>
      )
    },
    {
      key: "closes",
      label: "CLOSES",
      render: (item) => (
        <div className="table-col-stacked">
          <span className="table-col-primary">{item.closesDate}</span>
          <span className="table-col-secondary">{item.closesSub}</span>
        </div>
      )
    },
    {
      key: "flow",
      label: "FLOW",
      render: (item) => {
        let pillClass = "pill-queued";
        if (item.flowStatus === "Approval pending") pillClass = "pill-approval";
        else if (item.flowStatus === "Listening") pillClass = "pill-listening";
        else if (item.flowStatus === "Sending") pillClass = "pill-sending";

        return (
          <div className="table-col-stacked">
            <span className={`table-flow-pill ${pillClass}`}>{item.flowStatus}</span>
            <span className="table-col-secondary mt-1">{item.flowSub}</span>
          </div>
        );
      }
    },
    {
      key: "actions",
      label: "ACTIONS",
      render: (item) => {
        if (item.isLocked) {
          return (
            <div className="table-actions-locked">{talentPulse360.LOCKED}</div>
          );
        }
        return (
          <div className="table-actions-row">
            <button
              type="button"
              className="table-action-btn"
              onClick={() => handleOpenEditScheduledModal(item)}
            >
              Edit
            </button>
            <button
              type="button"
              className="table-action-btn"
              onClick={() => handleCancelScheduledSend(item)}
            >
              Cancel
            </button>
          </div>
        );
      }
    }
  ], [talentPulse360.LOCKED]);

  // Form states in Send Modal (Pulse Trigger)
  const [sendRecipients, setSendRecipients] = useState([]);
  const [isRecipientDropdownOpen, setIsRecipientDropdownOpen] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientSearchResults, setRecipientSearchResults] = useState([]);
  const [isSearchingRecipients, setIsSearchingRecipients] = useState(false);
  const [sendStartDateTime, setSendStartDateTime] = useState(() => formatDateTimeLocal(new Date()));
  const [sendEndDateTime, setSendEndDateTime] = useState(
    () => formatDateTimeLocal(getMinimumEndDateTime(sendStartDateTime))
  );
  const [isSendingSurvey, setIsSendingSurvey] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const sendToBoxRef = useRef(null);

  useOutsideClick(
    sendToBoxRef,
    useCallback(() => {
      setIsRecipientDropdownOpen(false);
    }, [])
  );

  useOutsideClick(
    editSendToBoxRef,
    useCallback(() => {
      setIsEditRecipientDropdownOpen(false);
    }, [])
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsRecipientDropdownOpen(false);
        setIsEditRecipientDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const AVAILABLE_RECIPIENT_OPTIONS = [
    { id: "opt-1", type: "COHO", text: "Commercial & Sales India · 420", count: 420 },
    { id: "opt-2", type: "COHO", text: "Agronomy & Technical Services · 180", count: 180 },
    { id: "opt-3", type: "COHO", text: "R&D Division · 210", count: 210 },
    { id: "opt-4", type: "COHO", text: "Operations & Supply Chain · 350", count: 350 },
    { id: "opt-5", type: "PERS", text: "Meera Joshi", count: 1 },
    { id: "opt-6", type: "PERS", text: "Ankit Sharma", count: 1 },
    { id: "opt-7", type: "PERS", text: "Priya Nair", count: 1 },
  ];

  const totalRecipientsCount = sendRecipients.length;

  useEffect(() => {
    if (!sendModalSurvey || !isRecipientDropdownOpen) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsSearchingRecipients(true);
      const result = await dispatch(fetchSurveyUsers({
        q: recipientSearch.trim(), type: "person", limit: 20,
      }));
      const { data, error } = normalizeActionResult(result);
      if (cancelled) return;
      if (error) {
        setRecipientSearchResults([]);
        toast.error(error.message || "Unable to search people.");
      } else {
        setRecipientSearchResults(getUserRows(data).map((user) => {
          const email = user.email || user.user_email || user.email_address || user.username;
          const name = user.full_name || user.name || user.display_name || email;
          return {
            ...user,
            id: user.id || user.user_id || email,
            email,
            name,
            type: "PERS",
            text: name,
            count: 1,
          };
        }).filter((user) => user.email));
      }
      setIsSearchingRecipients(false);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dispatch, sendModalSurvey, isRecipientDropdownOpen, recipientSearch]);

  // Form states in Create / Edit Survey Modal
  const [editingSurveyId, setEditingSurveyId] = useState(null);
  const [newSurveyTitle, setNewSurveyTitle] = useState("");
  const [newSurveyDesc, setNewSurveyDesc] = useState("");
  const [newSurveyTag] = useState("Custom Survey");
  const [newSurveyTagTheme] = useState("purple");
  const [newSurveyQuestions, setNewSurveyQuestions] = useState([
    { id: 1, type: "scale_5", text: "" },
    { id: 2, type: "scale_5", text: "" }
  ]);
  const [isSavingSurvey, setIsSavingSurvey] = useState(false);

  // Interactive preview state
  const [previewRatings, setPreviewRatings] = useState({});
  const [previewComments, setPreviewComments] = useState({});

  // Handlers
  const handleOpenPreview = (survey) => {
    setPreviewSurvey(survey);
    setPreviewRatings({});
    setPreviewComments({});
  };

  const handleOpenCreateModal = () => {
    setEditingSurveyId(null);
    setNewSurveyTitle("");
    setNewSurveyDesc("");
    setNewSurveyQuestions([
      { id: 1, type: "scale_5", text: "" },
      { id: 2, type: "scale_5", text: "" }
    ]);
    setIsCreateModalOpen(true);
  };

  const handleOpenSendModal = (survey) => {
    const now = new Date();
    now.setSeconds(0, 0);
    setSendModalSurvey(survey);
    setIsRecipientDropdownOpen(false);
    setSendRecipients([]);
    setRecipientSearch("");
    setRecipientSearchResults([]);
    const startDateTime = formatDateTimeLocal(now);
    setSendStartDateTime(startDateTime);
    setSendEndDateTime(formatDateTimeLocal(getMinimumEndDateTime(startDateTime)));
    if (survey.id) {
      dispatch(fetchTalentPulseSurveys({ id: survey.id }))
        .then(normalizeActionResult).then(({ data, error }) => {
          if (error) {
            toast.error(error.message || "Unable to load survey details.");
            return;
          }
          const details = data?.survey || data?.data?.survey || data?.data || data;
          if (details && typeof details === "object") {
            setSendModalSurvey((current) => current?.id === survey.id
              ? { ...current, ...details, id: survey.id, title: details.name || details.title || current.title }
              : current);
          }
        });
    }
  };

  const handleSendSurvey = async () => {
    if (!sendModalSurvey || isSendingSurvey) return;
    if (!sendRecipients.length) {
      toast.error("Add at least one person to send this survey.");
      return;
    }
    if (!sendStartDateTime || !sendEndDateTime || new Date(sendEndDateTime) < getMinimumEndDateTime(sendStartDateTime)) {
      toast.error("End date and time must be at least 1 hour after the start date and time.");
      return;
    }

    const toApiDateTime = (value) => `${value}:00+05:30`;
    setIsSendingSurvey(true);

    const payload = {
      survey_id: sendModalSurvey.survey_id || sendModalSurvey.id,
      recipients: sendRecipients.map(({ email, name }) => ({ email, name })),
      start_time: toApiDateTime(sendStartDateTime),
      end_time: toApiDateTime(sendEndDateTime),
    };

    if (Array.isArray(sendModalSurvey.questions) && sendModalSurvey.questions.length > 0) {
      payload.questions = mapQuestionsToPayload(sendModalSurvey.questions);
    }

    console.log(sendModalSurvey.questions, "sendModalSurvey.questions")
    const result = await dispatch(sendTalentPulseSurvey(payload));

    const { data, error } = normalizeActionResult(result);
    setIsSendingSurvey(false);

    if (error || !data) {
      toast.error(error?.message || "Unable to send survey. Please try again.");
      return;
    }

    const audienceSummary = sendRecipients.map(r => r.text.split('·')[0].trim()).join(", ") || "Custom Recipients";
    const newInProgress = {
      id: `prog-${Date.now()}`,
      title: `${sendModalSurvey.title} (${audienceSummary})`,
      formName: sendModalSurvey.title,
      sentDate: "Just now",
      deadline: "7 days remaining",
      responses: 0,
      totalTarget: totalRecipientsCount,
      responseRate: 0,
      sentiment: "Collecting...",
    };
    setInProgressList([newInProgress, ...inProgressList]);
    toast.success(`Survey "${sendModalSurvey.title}" scheduled successfully.`);
    setSendModalSurvey(null);

    // Refresh analysis surveys/sends list
    dispatch(fetchTalentPulseSurveySends({ page: 1, page_size: 10 }))
      .then(normalizeActionResult)
      .then(({ data: sendsData, error: sendsErr }) => {
        if (!sendsErr && sendsData) {
          setAnalysisSurveys(getSurveyRows(sendsData).filter((s) => s.survey_send_id || s.send_id || s.id || s.survey_id));
        }
      });
  };


  const handleSaveSurvey = async (e) => {
    if (e) e.preventDefault();
    if (isSavingSurvey) return;
    const surveyTitle = newSurveyTitle.trim();
    if (!surveyTitle) {
      toast.error("Please enter a survey name before publishing.");
      return;
    }

    const missingQuestion = newSurveyQuestions.find((question) => !question.text?.trim());
    if (missingQuestion) {
      toast.error("Please complete all question fields before publishing the survey.");
      return;
    }

    const invalidChoiceQuestion = newSurveyQuestions.find(
      (question) => normalizeQuestionType(question.type) === "single_choice"
        && (Array.isArray(question.options)
          ? question.options.length < 2
          : (question.options || "").split(",").map((option) => option.trim()).filter(Boolean).length < 2)
    );
    if (invalidChoiceQuestion) {
      toast.error("Add at least two choices separated by commas for every single choice question.");
      return;
    }

    if (!editingSurveyId) {
      const payload = {
        name: surveyTitle,
        description: newSurveyDesc.trim(),
        questions: mapQuestionsToPayload(newSurveyQuestions),
        is_anonymous: isAnonymous,
        action: "publish",
      };

      setIsSavingSurvey(true);
      const result = await dispatch(createTalentPulseSurvey(payload));
      const { data, error } = normalizeActionResult(result);
      setIsSavingSurvey(false);

      if (error || !data) {
        toast.error(error?.message || "Unable to create survey. Please try again.");
        return;
      }

      const created = {
        id: data.id || data.survey_id || `custom-${Date.now()}`,
        title: payload.name,
        tag: newSurveyTag,
        tagTheme: newSurveyTagTheme,
        iconType: "lightning",
        description: payload.description || "Custom team pulse survey for motivation, alignment, and feedback.",
        questionsCount: payload.questions.length,
        estimatedTime: `~${Math.max(1, Math.round(payload.questions.length * 0.5))} min to answer`,
        lastSent: "Published · Just now",
        questions: payload.questions,
      };
      setSurveyForms((forms) => [...forms, created]);
      toast.success(`Survey "${created.title}" published successfully!`);
      setIsCreateModalOpen(false);
      setEditingSurveyId(null);
      setNewSurveyTitle("");
      setNewSurveyDesc("");
      setNewSurveyQuestions([
        { id: 1, type: "scale_5", text: "" },
        { id: 2, type: "scale_5", text: "" },
      ]);
      return;
    }

    if (editingSurveyId) {
      const updatedForms = surveyForms.map(form => {
        if (form.id === editingSurveyId) {
          return {
            ...form,
            title: newSurveyTitle.trim(),
            description: newSurveyDesc.trim() || form.description,
            questionsCount: newSurveyQuestions.length,
            estimatedTime: `~${Math.max(1, Math.round(newSurveyQuestions.length * 0.5))} min to answer`,
            questions: mapQuestionsToPayload(newSurveyQuestions)
          };
        }
        return form;
      });
      setSurveyForms(updatedForms);
      toast.success(`Survey "${newSurveyTitle.trim()}" updated successfully!`);
    } else {
      const created = {
        id: `custom-${Date.now()}`,
        title: newSurveyTitle.trim(),
        tag: newSurveyTag,
        tagTheme: newSurveyTagTheme,
        iconType: newSurveyTagTheme === "purple" ? "lightning" : newSurveyTagTheme === "green" ? "user-group" : "smile",
        description: newSurveyDesc.trim() || "Custom team pulse survey for motivation, alignment, and feedback.",
        questionsCount: newSurveyQuestions.length,
        estimatedTime: `~${Math.max(1, Math.round(newSurveyQuestions.length * 0.5))} min to answer`,
        lastSent: "Draft · Ready",
        questions: mapQuestionsToPayload(newSurveyQuestions)
      };
      setSurveyForms([...surveyForms, created]);
      toast.success(`New survey "${created.title}" created successfully!`);
    }

    setIsCreateModalOpen(false);
    setEditingSurveyId(null);
    setNewSurveyTitle("");
    setNewSurveyDesc("");
    setNewSurveyQuestions([
      { id: 1, type: "scale_5", text: "" },
      { id: 2, type: "scale_5", text: "" }
    ]);
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!surveyId || isDeletingSurvey) return;
    setIsDeletingSurvey(true);
    const result = await dispatch(createTalentPulseSurvey({ id: surveyId, action: "delete" }));
    const { data, error } = normalizeActionResult(result);
    setIsDeletingSurvey(false);
    if (error || !data) {
      toast.error(error?.message || "Unable to delete survey. Please try again.");
      return;
    }
    setSurveyForms((forms) => forms.filter((item) => item.id !== surveyId));
    setDeleteConfirmSurvey(null);
    toast.info(talentPulse360.SURVEY_REMOVED || "Survey deleted successfully.");
  };


  const renderCardIcon = (iconType) => {
    return <span className="talent-survey-card-icon-symbol" aria-hidden="true">{CARD_ICONS[iconType] || CARD_ICONS.smile}</span>;
  };
  const displayedAnalysisWaves = selectedAnalysisSurvey?.waves.slice().reverse() || [];
  const selectedAnalysisWaveNumber = selectedAnalysisWave
    ? displayedAnalysisWaves.findIndex((wave) => wave.id === selectedAnalysisWave.id) + 1
    : 0;

  // Process cards returned by talentPulseSurveyDashboard
  const cardsList = useMemo(() => {
    return dashboardData?.cards || dashboardData?.data?.cards || [];
  }, [dashboardData]);
  const themesCardData = cardsList.find(
    (c) => c?.card === "themes" || c?.name?.toLowerCase().includes("theme")
  );
  const sentimentCardData = cardsList.find(
    (c) => c?.card === "sentiment" || c?.name?.toLowerCase().includes("sentiment")
  );
  const ratingsCardData = cardsList.find(
    (c) => c?.card === "ratings" || c?.name?.toLowerCase().includes("rating")
  );
  const actionsData = dashboardData?.actions || dashboardData?.data?.actions || null;

  const sortedCardsList = useMemo(() => {
    if (!cardsList || !cardsList.length) return [];
    return [...cardsList].sort((a, b) => {
      const posA = a?.position !== undefined && a?.position !== null ? Number(a.position) : 999;
      const posB = b?.position !== undefined && b?.position !== null ? Number(b.position) : 999;
      return posA - posB;
    });
  }, [cardsList]);

  // Helper: pick segment colour for bar charts
  const segmentColor = (seg) => {
    if (seg === "negative" || seg === "unfavourable") return "#ef4444";
    if (seg === "positive" || seg === "favourable") return "#10b981";
    if (seg === "neutral") return "#e87b00";
    return "#cbd5e1"; // other / evenly-split
  };

  // Live Card Detail data (from talentPulseSurveyCardsData API)
  const themesDetail = cardsDetailData?.themes?.detail || cardsDetailData?.themes?.data?.detail || cardsDetailData?.themes || null;
  const sentimentDetail = cardsDetailData?.sentiment?.detail || cardsDetailData?.sentiment?.data?.detail || cardsDetailData?.sentiment || null;
  const ratingsDetail = cardsDetailData?.ratings?.detail || cardsDetailData?.ratings?.data?.detail || cardsDetailData?.ratings || null;
  const ratingsScope = cardsDetailData?.ratings?.scope || cardsDetailData?.ratings?.data?.scope || null;

  const themesMap = useMemo(() => {
    const list = themesDetail?.themes || [];
    const map = {};
    list.forEach((t) => {
      if (t?.theme_key) map[t.theme_key] = t;
    });
    return map;
  }, [themesDetail]);

  const polarityDim = sentimentDetail?.dimensions?.find((d) => d.dimension === "polarity");
  const intensityDim = sentimentDetail?.dimensions?.find((d) => d.dimension === "intensity");
  const targetDim = sentimentDetail?.dimensions?.find((d) => d.dimension === "target");
  const temporalDim = sentimentDetail?.dimensions?.find((d) => d.dimension === "temporal");
  const constructiveDim = sentimentDetail?.dimensions?.find((d) => d.dimension === "constructive");
  const coverageData = sentimentDetail?.coverage || null;

  const sentimentDonutGradient = useMemo(() => {
    if (!polarityDim?.buckets || !polarityDim.buckets.length) {
      return "#e2e8f0";
    }
    const pos = polarityDim.buckets.find((b) => b.bucket === "positive")?.pct || 0;
    const mix = polarityDim.buckets.find((b) => b.bucket === "mixed")?.pct || 0;
    const neu = polarityDim.buckets.find((b) => b.bucket === "neutral")?.pct || 0;

    const stop1 = pos;
    const stop2 = stop1 + mix;
    const stop3 = stop2 + neu;

    return `conic-gradient(#079b6a 0% ${stop1}%, #e87b00 ${stop1}% ${stop2}%, #e87b00 ${stop2}% ${stop3}%, #e5262d ${stop3}% 100%)`;
  }, [polarityDim]);

  const activeWaveDateLabel = dashboardData?.sent_date || selectedAnalysisWave?.windowDates || "";

  return (
    <div className="talent-survey-page-container">
      <div className={`talent-survey-content${themePanelOpen ? " has-theme-panel-open" : ""}`}>

        {/* Page Header */}
        <div className="talent-survey-header">
          <div className="talent-survey-header-left">
            <h1 className="talent-survey-title">{talentPulse360.SURVEY_TITLE}</h1>
            <p className="talent-survey-subtitle">{talentPulse360.SURVEY_SUBTITLE}</p>
          </div>
          <div className="talent-survey-header-actions">
            <button
              type="button"
              className="button-style primary-button"
              onClick={handleOpenCreateModal}
            >
              <span className="button-leading-symbol" aria-hidden="true">+</span>
              <span>{talentPulse360.CREATE_SURVEY}</span>
            </button>
          </div>
        </div>

        {/* Tabs Bar */}
        <div className={`talent-survey-tabs-bar${activeTab === "analysis" && selectedAnalysisSurvey ? " has-analysis-detail" : ""}`} role="tablist">
          <button
            className={`talent-survey-tab ${activeTab === "forms" ? "active" : ""}`}
            onClick={() => setActiveTab("forms")}
            role="tab"
          >
            <span>Survey forms</span>
            <span className="talent-survey-tab-badge">{surveyForms.length}</span>
          </button>
          {/* <button
            className={`talent-survey-tab ${activeTab === "scheduled" ? "active" : ""}`}
            onClick={() => setActiveTab("scheduled")}
            role="tab"
          >
            <span>Scheduled</span>
            <span className="talent-survey-tab-badge">{scheduledList.length}</span>
          </button>
          <button
            className={`talent-survey-tab ${activeTab === "in_progress" ? "active" : ""}`}
            onClick={() => setActiveTab("in_progress")}
            role="tab"
          >
            <span>In progress</span>
            <span className="talent-survey-tab-badge">{inProgressList.length}</span>
          </button> */}
          <button
            className={`talent-survey-tab ${activeTab === "analysis" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("analysis");
              resetAnalysisState();
            }}
            role="tab"
            aria-selected={activeTab === "analysis"}
          >
            <span>Results and Analysis</span>
            <span className="talent-survey-tab-badge">{analysisSurveys.length}</span>
          </button>
        </div>

        {/* Tab 1: Survey forms Grid */}
        {activeTab === "forms" && (
          surveysLoading ? (
            <div className="talent-survey-loading-wrapper">
              <LoadingScreen isLoading={true} loadingText="Loading survey forms..." />
            </div>
          ) : surveyForms.length === 0 ? (
            <div className="talent-survey-empty-state">
              <h3 className="talent-survey-empty-title">{noDataText}</h3>
              <p className="talent-survey-empty-desc">There are no survey forms available.</p>
            </div>
          ) : (
            <div className="talent-survey-grid">
              {surveyForms.map((item) => (
                <div className="talent-survey-card" key={item.id}>

                  {/* Card Top Row: Icon + Badge Pill */}
                  <div className="talent-survey-card-header">
                    <div className={`talent-survey-card-icon ${item.tagTheme}`}>
                      {renderCardIcon(item.iconType)}
                    </div>
                    <span className={`talent-survey-pill ${item.tagTheme}`}>
                      {item.tag}
                    </span>
                  </div>

                  {/* Card Body */}
                  <div className="talent-survey-card-body">
                    <h3 className="talent-survey-card-title">{item.title}</h3>
                    <p className="talent-survey-card-desc">{item.description}</p>

                    {/* Meta Line */}
                    <div className="talent-survey-card-meta">
                      <span className="talent-survey-card-meta-item">{item.questionsCount} questions</span>
                      <span className="talent-survey-card-meta-item">~{item.estimatedTime.replace('~', '')}</span>
                      <span className="talent-survey-card-meta-item highlight-date">
                        {item.lastSent.startsWith("Daily") ? item.lastSent : `Last sent ${item.lastSent}`}
                      </span>
                    </div>
                  </div>

                  {/* Card Action Buttons */}
                  <div className="talent-survey-card-actions">
                    <button
                      type="button"
                      className="button-style primary-button"
                      onClick={() => handleOpenSendModal(item)}
                    >
                      Send
                    </button>
                    <button
                      type="button"
                      className="button-style primary-grey-button"
                      onClick={() => handleOpenPreview(item)}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      className="button-style talent-survey-btn-delete"
                      onClick={() => setDeleteConfirmSurvey(item)}
                      title="Delete survey form"
                      aria-label="Delete survey form"
                    >
                      <img src={deleteIcon} alt="" />
                    </button>
                  </div>

                </div>
              ))}
            </div>
          )
        )}

        {/* Tab 2: Scheduled Tab */}
        {activeTab === "scheduled" && (
          <div className="talent-survey-scheduled-container">
            <div className="scheduled-table-header">
              <div className="scheduled-table-header-left">
                <h3>Scheduled sends</h3>
                <p>Queued in Power Automate. A send can be edited or cancelled until it starts — once cards begin going out, it cannot be recalled.</p>
              </div>
            </div>

            <MasterTable
              data={scheduledList}
              columns={scheduledColumns}
              rowKey="id"
              className="scheduled-master-table"
              pageSize={10}
              isLoading={scheduledLoading}
              loadingMessage="Loading scheduled sends..."
              emptyMessage={noDataText}
            />

            <div className="scheduled-table-footer">
              A send locks the moment cards start going out. Adaptive cards cannot be recalled once delivered, so nothing is editable or cancellable after that point.
            </div>
          </div>
        )}

        {/* Tab 3: In Progress Tab */}
        {activeTab === "in_progress" && (
          <div className="talent-survey-scheduled-container">
            <div className="scheduled-table-header">
              <div className="scheduled-table-header-left">
                <h3>Open right now</h3>
                <p>Status reported by the flow per recipient. Responses land in the SharePoint list and sync to <code className="inline-code">pulse.responses</code>.</p>
              </div>
            </div>

            <MasterTable
              data={inProgressList}
              columns={inProgressColumns}
              rowKey="id"
              className="scheduled-master-table"
              pageSize={10}
              isLoading={inProgressLoading}
              loadingMessage="Loading in-progress surveys..."
              emptyMessage={noDataText}
            />
          </div>
        )}

        {activeTab === "analysis" && (
          selectedAnalysisSurvey ? (
            <section className={`talent-survey-detail${selectedAnalysisWave ? " has-wave-analysis" : ""}`} aria-label={`${selectedAnalysisSurvey.surveyTitle} wave analysis`}>
              <nav className="talent-survey-breadcrumb" aria-label="Breadcrumb">
                <button
                  type="button"
                  className="button-style talent-survey-breadcrumb-item"
                  onClick={resetAnalysisState}
                >
                  Results and Analysis
                </button>
                <span className="talent-survey-breadcrumb-sep" aria-hidden="true">›</span>
                <button
                  type="button"
                  className={`button-style talent-survey-breadcrumb-item${!selectedAnalysisWave ? " is-current" : ""}`}
                  onClick={handleBackToSurveyWaves}
                  aria-current={!selectedAnalysisWave ? "page" : undefined}
                  disabled={!selectedAnalysisWave}
                >
                  {selectedAnalysisSurvey.surveyTitle}
                </button>
                {selectedAnalysisWave && (
                  <>
                    <span className="talent-survey-breadcrumb-sep" aria-hidden="true">›</span>
                    <span className="talent-survey-breadcrumb-item is-current" aria-current="page">
                      Analysis
                    </span>
                  </>
                )}
              </nav>
              <div className="talent-survey-detail-heading">
                <div>
                  <h2>{dashboardData?.heading || (selectedAnalysisWave ? `Wave ${selectedAnalysisWaveNumber}` : selectedAnalysisSurvey?.surveyTitle)}</h2>
                  <p>{dashboardData?.sent_date
                    ? `Sent on ${dashboardData.sent_date}`
                    : selectedAnalysisWave?.windowDates
                      ? `${selectedAnalysisWave.responsesStr || ""} responses (${selectedAnalysisWave.responsesPct || ""}), closed ${selectedAnalysisWave.windowDates}.`
                      : selectedAnalysisSurvey?.surveySub || ""}</p>
                </div>
              </div>
              {selectedAnalysisWave ? (
                <div className="talent-survey-wave-analysis-view">
                  {/* <div className="talent-survey-wave-switcher" aria-label="Select survey wave">
                    <span>Wave</span>
                    {displayedAnalysisWaves.map((wave, index) => (
                      <button
                        type="button"
                        key={wave.id}
                        className={wave.id === selectedAnalysisWave.id ? "is-active" : ""}
                        onClick={() => {
                          setSelectedAnalysisWaveId(wave.id);
                          setSelectedAnalysisWave(wave);
                          if (themePanelOpen && analysisPanelType) {
                            const waveSurveyId =
                              wave?.survey_send_id ||
                              wave?.send_id ||
                              wave?.id ||
                              selectedAnalysisSurvey?.survey_send_id ||
                              selectedAnalysisSurvey?.send_id ||
                              selectedAnalysisSurvey?.id;
                            fetchCardDetail(analysisPanelType, waveSurveyId);
                          }
                        }}
                        aria-pressed={wave.id === selectedAnalysisWave.id}
                      >
                        {index + 1} · {wave.windowDates.match(/[A-Za-z]{3}/)?.[0] || wave.windowDates}
                      </button>
                    ))}
                  </div> */}
                  <div className={`talent-survey-analysis-cards${dashboardLoading ? " is-loading" : ""}`}>
                    {sortedCardsList.map((cardData, idx) => {
                      const cardType = cardData?.card || (
                        cardData?.name?.toLowerCase().includes("sentiment")
                          ? "sentiment"
                          : cardData?.name?.toLowerCase().includes("rating")
                            ? "ratings"
                            : "themes"
                      );
                      const isSelected = themePanelOpen && analysisPanelType === cardType;
                      const displayPos = cardData?.position !== undefined && cardData?.position !== null ? cardData.position : idx + 1;

                      if (cardType === "themes") {
                        return (
                          <article
                            key={cardData.card || `themes-${idx}`}
                            className={`talent-survey-analysis-card${isSelected ? " is-selected" : ""}`}
                            onClick={() => handleOpenAnalysisCard("themes")}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleOpenAnalysisCard("themes");
                              }
                            }}
                          >
                            <div className="talent-survey-analysis-card-meta">
                              <span>{displayPos}</span>
                            </div>
                            <h4>{cardData.name}</h4>
                            <div className="talent-survey-analysis-metric">
                              {cardData.stat?.value !== undefined ? cardData.stat.value : ""}
                              {cardData.stat && (
                                <span>
                                  {cardData.stat.unit
                                    ? `${cardData.stat.unit}${cardData.stat.mentions_total ? ` · ${cardData.stat.mentions_total}` : ""}`
                                    : cardData.stat.mentions_total || ""}
                                </span>
                              )}
                            </div>
                            {cardData.bar && Array.isArray(cardData.bar) && cardData.bar.length > 0 && (
                              <div className="talent-survey-analysis-bar" aria-label="Theme sentiment distribution">
                                {cardData.bar.map((segment, sIdx) => (
                                  <i
                                    key={sIdx}
                                    style={{
                                      width: segment.pct ? `${segment.pct}%` : undefined,
                                      backgroundColor: segmentColor(segment.segment),
                                      borderRadius:
                                        sIdx === 0 ? "4px 0 0 4px"
                                          : sIdx === cardData.bar.length - 1 ? "0 4px 4px 0"
                                            : "0",
                                    }}
                                    title={`${segment.label || segment.segment || ""}: ${segment.pct || 0}%`}
                                  />
                                ))}
                              </div>
                            )}
                            {cardData.body && <p>{cardData.body}</p>}
                            <div className="talent-survey-analysis-card-footer">
                              {cardData.pill?.text && <span>{cardData.pill.text}</span>}
                              <button
                                type="button"
                                aria-pressed={isSelected}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAnalysisCard("themes");
                                }}
                              >
                                Open
                              </button>
                            </div>
                          </article>
                        );
                      }

                      if (cardType === "sentiment") {
                        return (
                          <article
                            key={cardData.card || `sentiment-${idx}`}
                            className={`talent-survey-analysis-card${isSelected ? " is-selected" : ""}`}
                            onClick={() => handleOpenAnalysisCard("sentiment")}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleOpenAnalysisCard("sentiment");
                              }
                            }}
                          >
                            <div className="talent-survey-analysis-card-meta">
                              <span>{displayPos}</span>
                            </div>
                            <h4>{cardData.name}</h4>
                            <div className={`talent-survey-analysis-metric${cardData.stat?.bucket === "negative" ? " is-negative" : cardData.stat?.bucket === "positive" ? " is-positive" : ""}`}>
                              {cardData.stat?.value !== undefined ? cardData.stat.value : ""}
                              {cardData.stat?.unit && (
                                <span className="talent-survey-analysis-metric-unit">{cardData.stat.unit}</span>
                              )}
                            </div>
                            {cardData.stat?.coded !== undefined && (
                              <p className="talent-survey-analysis-coded-sub">
                                {cardData.stat.coded} {cardData.stat.scope || "comments"} coded
                              </p>
                            )}
                            {cardData.body && <p>{cardData.body}</p>}
                            <div className="talent-survey-analysis-card-footer">
                              {cardData.pill?.text && <span className="is-mixed">{cardData.pill.text}</span>}
                              <button
                                type="button"
                                aria-pressed={isSelected}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAnalysisCard("sentiment");
                                }}
                              >
                                Open
                              </button>
                            </div>
                          </article>
                        );
                      }

                      if (cardType === "ratings") {
                        return (
                          <article
                            key={cardData.card || `ratings-${idx}`}
                            className={`talent-survey-analysis-card${isSelected ? " is-selected" : ""}`}
                            onClick={() => handleOpenAnalysisCard("ratings")}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleOpenAnalysisCard("ratings");
                              }
                            }}
                          >
                            <div className="talent-survey-analysis-card-meta">
                              <span>{displayPos}</span>
                            </div>
                            <h4>{cardData.name}</h4>
                            <div className="talent-survey-analysis-metric">
                              {cardData.stat?.value !== undefined ? cardData.stat.value : "—"}
                              {cardData.stat?.unit && (
                                <span className="talent-survey-analysis-metric-unit">{cardData.stat.unit}</span>
                              )}
                            </div>
                            {cardData.bar && Array.isArray(cardData.bar) && cardData.bar.length > 0 && (
                              <div className="talent-survey-analysis-bar" aria-label="Rating distribution">
                                {cardData.bar.map((seg, sIdx) => (
                                  <i
                                    key={sIdx}
                                    style={{
                                      width: `${seg.pct}%`,
                                      backgroundColor: segmentColor(seg.segment),
                                      borderRadius:
                                        sIdx === 0 ? "4px 0 0 4px"
                                          : sIdx === cardData.bar.length - 1 ? "0 4px 4px 0"
                                            : "0",
                                    }}
                                    title={`${seg.label || seg.segment || ""}: ${seg.pct}%`}
                                  />
                                ))}
                              </div>
                            )}
                            {cardData.body && <p>{cardData.body}</p>}
                            <div className="talent-survey-analysis-card-footer">
                              {cardData.pill?.text && <span>{cardData.pill.text}</span>}
                              <button
                                type="button"
                                aria-pressed={isSelected}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAnalysisCard("ratings");
                                }}
                              >
                                Open
                              </button>
                            </div>
                          </article>
                        );
                      }

                      return null;
                    })}
                  </div>

                  {/* Recommended Actions section */}
                  {actionsData && actionsData.count > 0 && (
                    <div className="talent-survey-actions-section">
                      <div className="talent-survey-actions-header">
                        <div>
                          <h3 className="talent-survey-actions-title">{actionsData.name || "Recommended actions"}</h3>
                          <p className="talent-survey-actions-subtitle">{actionsData.headline || actionsData.body || ""}</p>
                        </div>
                        <div className="talent-survey-actions-meta">
                          {actionsData.pill && (
                            <span className={`talent-survey-pill talent-survey-actions-pill ${actionsData.pill.tone || "neutral"}`}>
                              {actionsData.pill.text}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="talent-survey-actions-groups">
                        {(actionsData.groups || []).filter(g => g.count > 0).map((group) => (
                          <div key={group.source_card} className="talent-survey-actions-group">
                            <div className="talent-survey-actions-group-label">
                              <span className={`talent-survey-actions-source-badge source-${group.source_card}`}>{group.name}</span>
                              <span>{group.label}</span>
                            </div>
                            <ol className="talent-survey-actions-list">
                              {group.actions.map((item, i) => (
                                <li key={i}>
                                  <span className="talent-survey-action-num">{i + 1}</span>
                                  <p>{item.action}</p>
                                </li>
                              ))}
                            </ol>
                          </div>
                        ))}
                      </div>
                      {actionsData.model_written_note && (
                        <p className="talent-survey-actions-footnote">✦ {actionsData.model_written_note}</p>
                      )}
                    </div>
                  )}

                  <Modal
                    open={themePanelOpen}
                    onClose={() => setThemePanelOpen(false)}
                    side="right"
                    width="30%"
                    hasBackdrop={false}
                    belowHeader={true}
                    className={`talent-survey-theme-panel${analysisPanelType === "sentiment" ? " is-sentiment-panel" : analysisPanelType === "ratings" ? " is-ratings-panel" : ""}`}
                    aria-label={`${analysisPanelType === "sentiment" ? "Sentiment classification" : analysisPanelType === "ratings" ? "Rating analysis" : "Theme extraction"} details`}
                  >
                    <header className="talent-survey-theme-panel-header">
                      <div>
                        <h3>
                          {analysisPanelType === "sentiment"
                            ? (sentimentDetail?.name || sentimentCardData?.name || "")
                            : analysisPanelType === "ratings"
                              ? (ratingsDetail?.name || ratingsCardData?.name || "Rating analysis")
                              : (themesDetail?.name || themesCardData?.name || "")}
                        </h3>
                        <p>
                          {analysisPanelType === "sentiment"
                            ? (sentimentDetail?.subline || (sentimentCardData?.stat?.coded ? `${sentimentCardData.stat.coded} ${sentimentCardData.stat.scope || "comments"} coded` : ""))
                            : analysisPanelType === "ratings"
                              ? (ratingsDetail?.subline || (ratingsCardData?.stat?.questions_asked ? `${ratingsCardData.stat.questions_asked} scale ${ratingsCardData.stat.questions_asked === 1 ? "question" : "questions"}` : ""))
                              : (themesDetail?.subline || (themesCardData?.stat ? `${themesCardData.stat.value ?? ""} ${themesCardData.stat.unit || ""} · ${themesCardData.stat.mentions_total || ""}` : ""))}
                        </p>
                      </div>
                      <div className="talent-survey-theme-panel-controls">
                        <button type="button" aria-label="Close panel" title="Close" onClick={() => setThemePanelOpen(false)}>×</button>
                      </div>
                      <nav className="talent-survey-theme-panel-tabs" aria-label="Theme panel tabs">
                        <button type="button" className={themePanelTab === "insight" ? "is-active" : ""} onClick={() => setThemePanelTab("insight")}>Insight</button>
                        <button type="button" className={themePanelTab === "ask" ? "is-active" : ""} disabled>Ask</button>
                      </nav>
                    </header>

                    {/* === RATINGS PANEL === */}
                    {analysisPanelType === "ratings" ? (themePanelTab === "insight" ? (
                      cardDetailLoading && !ratingsDetail ? (
                        <div className="talent-survey-theme-panel-content talent-survey-panel-loading">
                          <div className="talent-survey-spinner" />
                          <span>Loading rating analysis details...</span>
                        </div>
                      ) : (
                        <div className="talent-survey-theme-panel-content talent-survey-ratings-content">
                          {/* <span className="talent-survey-theme-wave-label">
                            {dashboardData?.heading || `Wave ${selectedAnalysisWaveNumber}`}{activeWaveDateLabel ? ` · ${activeWaveDateLabel}` : ""}
                          </span> */}

                          {/* {(ratingsDetail?.title || ratingsDetail?.headline) && (
                            <div className="talent-survey-panel-headline-box">
                              <h5>{ratingsDetail.title || ratingsDetail.headline}</h5>
                              {ratingsDetail.subline && <p>{ratingsDetail.subline}</p>}
                            </div>
                          )} */}

                          {ratingsCardData && (
                            <div className="talent-survey-ratings-summary">
                              <div className={`talent-survey-ratings-big-stat${ratingsCardData?.stat?.value !== undefined && ratingsCardData.stat.value < 30 ? " is-low" : ""}`}>
                                <b>{ratingsCardData?.stat?.value ?? "—"}</b>
                                {ratingsCardData?.stat?.unit && <span>{ratingsCardData.stat.unit}</span>}
                              </div>
                              {ratingsCardData?.stat?.questions_published !== undefined && ratingsCardData?.stat?.questions_asked !== undefined && (
                                <p className="talent-survey-ratings-scope">
                                  {ratingsCardData.stat.questions_published} of {ratingsCardData.stat.questions_asked} scale {ratingsCardData.stat.questions_asked === 1 ? "question" : "questions"} published
                                </p>
                              )}
                            </div>
                          )}

                          {ratingsCardData?.bar && Array.isArray(ratingsCardData.bar) && ratingsCardData.bar.length > 0 && (
                            <section className="talent-survey-ratings-bar-breakdown">
                              <h4>Overall Distribution</h4>
                              {ratingsCardData.bar.map((seg, i) => (
                                <div key={i} className="talent-survey-ratings-bar-row">
                                  <span>{seg.label || seg.segment}</span>
                                  <div className="talent-survey-ratings-bar-track">
                                    <div
                                      className={`talent-survey-ratings-bar-fill seg-${seg.segment}`}
                                      style={{ width: `${seg.pct}%`, backgroundColor: segmentColor(seg.segment) }}
                                    />
                                  </div>
                                  <b>{seg.pct}%</b>
                                </div>
                              ))}
                            </section>
                          )}

                          {ratingsCardData?.body && <p className="talent-survey-ratings-body">{ratingsCardData.body}</p>}

                          {/* Questions breakdown */}
                          {ratingsDetail?.questions && Array.isArray(ratingsDetail.questions) && ratingsDetail.questions.length > 0 && (
                            <section className="talent-survey-ratings-questions-section">
                              <div className="talent-survey-ratings-questions-header">
                                <h4>Questions ({ratingsDetail.questions.length})</h4>
                                {ratingsDetail.ordered_by && (
                                  <span className="talent-survey-ratings-order-tag">{ratingsDetail.ordered_by}</span>
                                )}
                              </div>

                              <div className="talent-survey-ratings-questions-list">
                                {ratingsDetail.questions.map((q, qIdx) => (
                                  <div className="talent-survey-ratings-question-card" key={q.question_id || qIdx}>
                                    <div className="talent-survey-ratings-q-header">
                                      <div className="talent-survey-ratings-q-title-wrap">
                                        <span className="talent-survey-ratings-q-rank">#{q.rank_by_favourable ?? qIdx + 1}</span>
                                        <h5 className="talent-survey-ratings-q-title">{q.question}</h5>
                                      </div>
                                      <div className="talent-survey-ratings-q-badges">
                                        {q.tone && (
                                          <span className={`talent-survey-tone-badge tone-${q.tone}`}>
                                            {q.tone}
                                          </span>
                                        )}
                                        {q.favourable_pct !== undefined && (
                                          <span className="talent-survey-ratings-favour-badge">
                                            <b>{q.favourable_pct}%</b> favourable
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="talent-survey-ratings-q-meta">
                                      {q.mean_label && <span className="talent-survey-q-mean">★ {q.mean_label}</span>}
                                      {q.scale_label && <span className="talent-survey-q-scale">Scale: {q.scale_label}</span>}
                                      {q.respondents_label && <span className="talent-survey-q-respondents">{q.respondents_label}</span>}
                                    </div>

                                    {q.bands && Array.isArray(q.bands) && q.bands.length > 0 && (
                                      <div className="talent-survey-ratings-q-bar" aria-label="Question score bands">
                                        {q.bands.map((b, bIdx) => (
                                          <i
                                            key={bIdx}
                                            style={{
                                              width: `${b.pct}%`,
                                              backgroundColor: segmentColor(b.segment),
                                              borderRadius:
                                                bIdx === 0
                                                  ? "4px 0 0 4px"
                                                  : bIdx === q.bands.length - 1
                                                    ? "0 4px 4px 0"
                                                    : "0",
                                            }}
                                            title={`${b.label || b.segment}: ${b.pct}% (${b.count} answers)`}
                                          />
                                        ))}
                                      </div>
                                    )}

                                    {q.bands && Array.isArray(q.bands) && q.bands.length > 0 && (
                                      <div className="talent-survey-ratings-q-legends">
                                        {q.bands.map((b, bIdx) => (
                                          <div key={bIdx} className={`talent-survey-ratings-legend-item seg-${b.segment}`}>
                                            <span className="legend-dot" style={{ backgroundColor: segmentColor(b.segment) }} />
                                            <span className="legend-label">{b.label || b.segment}</span>
                                            <b className="legend-pct">{b.pct}%</b>
                                            {b.count !== undefined && <small className="legend-count">({b.count})</small>}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {q.scale_note && <p className="talent-survey-q-note">{q.scale_note}</p>}
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}

                          {ratingsDetail?.band_rule && (
                            <div className="talent-survey-ratings-methodology-box">
                              <h6>Scoring Band Rule</h6>
                              <p>{ratingsDetail.band_rule}</p>
                              {ratingsDetail.base_note && <small className="talent-survey-rule-sub">{ratingsDetail.base_note}</small>}
                            </div>
                          )}

                          {ratingsDetail?.denominator_note && (
                            <p className="talent-survey-theme-denominator-note">• &nbsp;{ratingsDetail.denominator_note}</p>
                          )}

                          {ratingsScope?.country_note && (
                            <p className="talent-survey-theme-denominator-note talent-survey-scope-note">• &nbsp;{ratingsScope.country_note}</p>
                          )}
                        </div>
                      )
                    ) : (
                      <div className="talent-survey-theme-ask">
                        <p>Ask a question about ratings</p>
                        <textarea aria-label="Ask about rating analysis" placeholder="e.g. Which question scored lowest and why?" />
                        <button type="button" disabled>Ask about ratings</button>
                      </div>
                    ))

                      /* === SENTIMENT PANEL === */
                      : analysisPanelType === "sentiment" ? (themePanelTab === "insight" ? (
                        cardDetailLoading && !sentimentDetail ? (
                          <div className="talent-survey-theme-panel-content talent-survey-panel-loading">
                            <div className="talent-survey-spinner" />
                            <span>Loading sentiment classification details...</span>
                          </div>
                        ) : (
                          <div className="talent-survey-theme-panel-content talent-survey-sentiment-content">
                            <span className="talent-survey-theme-wave-label">
                              {dashboardData?.heading || `Wave ${selectedAnalysisWaveNumber}`}{activeWaveDateLabel ? ` · ${activeWaveDateLabel}` : ""}
                            </span>

                            <div className="talent-survey-sentiment-summary">
                              <div className="talent-survey-sentiment-donut" style={{ background: sentimentDonutGradient }}>
                                <span>
                                  <b>
                                    {polarityDim?.buckets?.find((b) => b.bucket === "negative")?.pct !== undefined
                                      ? `${polarityDim.buckets.find((b) => b.bucket === "negative").pct}%`
                                      : sentimentCardData?.stat?.value !== undefined
                                        ? `${sentimentCardData.stat.value}${sentimentCardData.stat.unit?.startsWith("%") ? "%" : ""}`
                                        : ""}
                                  </b>
                                  <small>{sentimentCardData?.stat?.bucket?.toUpperCase() || ""}</small>
                                </span>
                              </div>
                              {polarityDim?.buckets && polarityDim.buckets.length > 0 && (
                                <ul>
                                  {polarityDim.buckets.map((item) => {
                                    const colorClass =
                                      item.bucket === "positive"
                                        ? "positive"
                                        : item.bucket === "mixed"
                                          ? "mixed"
                                          : item.bucket === "neutral"
                                            ? "neutral"
                                            : "negative";
                                    return (
                                      <li key={item.label || item.bucket} className={`sentiment-item-${colorClass}`}>
                                        <i className={colorClass} />
                                        <span>{item.label}</span>
                                        <b>{item.pct}%</b>
                                        {item.count !== undefined && <small>{item.count}</small>}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>

                            {(sentimentDetail?.headline || sentimentDetail?.subline || sentimentCardData?.stat?.coded) && (
                              <p className="talent-survey-sentiment-takeaway">
                                • &nbsp;{sentimentDetail?.headline || sentimentDetail?.subline || `${sentimentCardData.stat.coded} ${sentimentCardData.stat.scope || "comments"} coded`}
                              </p>
                            )}

                            {/* Who the feeling is about */}
                            {targetDim && targetDim.buckets && targetDim.buckets.length > 0 && (
                              <section className="talent-survey-sentiment-box">
                                <h4>{targetDim.title || "Who the feeling is about"}</h4>
                                {targetDim.buckets
                                  .filter((b) => b.count > 0 || b.pct > 0)
                                  .sort((a, b) => b.pct - a.pct)
                                  .map((item) => {
                                    const maxTarget = Math.max(...targetDim.buckets.map((b) => b.pct), 1);
                                    return (
                                      <div className="talent-survey-sentiment-row" key={item.label || item.bucket}>
                                        <div>
                                          <span>{item.label}</span>
                                          <b>{item.pct}%</b>
                                        </div>
                                        <i>
                                          <em style={{ width: `${(item.pct / maxTarget) * 100}%` }} />
                                        </i>
                                      </div>
                                    );
                                  })}
                                {targetDim.note && <p>{targetDim.note}</p>}
                              </section>
                            )}

                            {/* Direction of travel */}
                            {temporalDim && temporalDim.buckets && temporalDim.buckets.length > 0 && (
                              <section className="talent-survey-sentiment-box">
                                <h4>{temporalDim.title || "Direction of travel"}</h4>
                                {temporalDim.buckets.map((item) => {
                                  const maxTemporal = Math.max(...temporalDim.buckets.map((b) => b.pct), 1);
                                  return (
                                    <div className="talent-survey-sentiment-row" key={item.label || item.bucket}>
                                      <div>
                                        <span>{item.label}</span>
                                        <b>{item.pct}%</b>
                                      </div>
                                      <i>
                                        <em style={{ width: `${(item.pct / maxTemporal) * 100}%` }} />
                                      </i>
                                    </div>
                                  );
                                })}
                                {temporalDim.note && <p>{temporalDim.note}</p>}
                              </section>
                            )}

                            {/* Strength of feeling (intensity) */}
                            {intensityDim && intensityDim.buckets && intensityDim.buckets.length > 0 && (
                              <section className="talent-survey-sentiment-box">
                                <h4>{intensityDim.title || "Strength of feeling"}</h4>
                                {intensityDim.buckets
                                  .filter((b) => b.count > 0 || b.pct > 0)
                                  .map((item) => {
                                    const maxIntensity = Math.max(...intensityDim.buckets.map((b) => b.pct), 1);
                                    const intensityColor =
                                      Number(item.bucket) >= 4 ? "#ef4444" : Number(item.bucket) >= 3 ? "#f59e0b" : "var(--survey-blue, #515be0)";
                                    return (
                                      <div className="talent-survey-sentiment-row" key={item.label || item.bucket}>
                                        <div>
                                          <span>{item.label}</span>
                                          <b>{item.pct}%</b>
                                        </div>
                                        <i>
                                          <em
                                            style={{
                                              width: `${(item.pct / maxIntensity) * 100}%`,
                                              background: intensityColor,
                                            }}
                                          />
                                        </i>
                                      </div>
                                    );
                                  })}
                                {intensityDim.note && <p>{intensityDim.note}</p>}
                              </section>
                            )}

                            {/* Whether a change was proposed (constructive) */}
                            {constructiveDim && constructiveDim.buckets && constructiveDim.buckets.length > 0 && (
                              <section className="talent-survey-sentiment-box talent-survey-sentiment-fix">
                                <h4>{constructiveDim.title || "Whether a change was proposed"}</h4>
                                {constructiveDim.buckets.find((b) => b.bucket === "true")?.pct !== undefined && (
                                  <strong>{constructiveDim.buckets.find((b) => b.bucket === "true").pct}%</strong>
                                )}
                                <p>
                                  {constructiveDim.buckets.find((b) => b.bucket === "true")?.label || ""}
                                  {constructiveDim.buckets.find((b) => b.bucket === "true")?.count !== undefined && constructiveDim.denominator_value !== undefined
                                    ? ` (${constructiveDim.buckets.find((b) => b.bucket === "true").count} of ${constructiveDim.denominator_value}).`
                                    : ""}
                                  {constructiveDim.note ? ` ${constructiveDim.note}` : ""}
                                </p>
                              </section>
                            )}

                            {/* Coverage details */}
                            {coverageData && (
                              <div className="talent-survey-coverage-box">
                                <div className="talent-survey-coverage-header">
                                  <span>Coded comments coverage</span>
                                  <b>{coverageData.coverage_pct}%</b>
                                </div>
                                <div className="talent-survey-coverage-bar">
                                  <div style={{ width: `${coverageData.coverage_pct}%` }} />
                                </div>
                                {coverageData.note && <p>{coverageData.note}</p>}
                              </div>
                            )}

                            {sentimentDetail?.denominator_note && (
                              <p className="talent-survey-theme-denominator-note">• &nbsp;{sentimentDetail.denominator_note}</p>
                            )}
                          </div>
                        )
                      ) : <div className="talent-survey-theme-ask"><p>Ask a question about sentiment</p><textarea aria-label="Ask about sentiment classification" placeholder="e.g. What is driving negative sentiment?" /><button type="button" disabled>Ask about sentiment</button></div>)

                        /* === THEMES PANEL === */
                        : themePanelTab === "insight" ? (
                          cardDetailLoading && !themesDetail ? (
                            <div className="talent-survey-theme-panel-content talent-survey-panel-loading">
                              <div className="talent-survey-spinner" />
                              <span>Loading theme extraction details...</span>
                            </div>
                          ) : (
                            <div className="talent-survey-theme-panel-content">
                              <span className="talent-survey-theme-wave-label">
                                {dashboardData?.heading || `Wave ${selectedAnalysisWaveNumber}`}{activeWaveDateLabel ? ` · ${activeWaveDateLabel}` : ""}
                              </span>
                              {/* {themesDetail?.headline && (
                                <div className="talent-survey-panel-headline-box">
                                  <h5>{themesDetail.headline}</h5>
                                  {themesDetail.subline && <p>{themesDetail.subline}</p>}
                                </div>
                              )} */}

                              {themesDetail?.groups && themesDetail.groups.length > 0 && (
                                themesDetail.groups.map((group) => {
                                  const groupKey = group.key || "negative";
                                  const groupClass =
                                    groupKey === "positive"
                                      ? "is-positive"
                                      : groupKey === "unsided" || groupKey === "neutral"
                                        ? "is-unsided"
                                        : "is-negative";
                                  return (
                                    <div className={`talent-survey-theme-group ${groupClass}`} key={group.key || group.label}>
                                      <h4>
                                        <i />
                                        {group.label || group.key}
                                      </h4>
                                      {group.theme_keys?.map((tKey) => {
                                        const item = themesMap[tKey];
                                        if (!item) return null;
                                        const reachPct = Number(item.reach_pct) || 0;
                                        const trackWidth = Math.min(100, Math.max(0, reachPct));
                                        return (
                                          <div className="talent-survey-theme-item" key={tKey}>
                                            <div className="talent-survey-theme-item-title">
                                              <strong>{item.theme}</strong>
                                              <b>{item.mentions}</b>
                                            </div>
                                            <div className="talent-survey-theme-track">
                                              <i style={{ width: `${trackWidth}%` }} />
                                            </div>
                                            <small>
                                              {`${item.respondents ?? 0} ${Number(item.respondents ?? 0) === 1 ? "respondent" : "respondents"}${item.reach_pct !== undefined && item.reach_pct !== null ? ` · ${item.reach_pct}%` : ""}`}
                                            </small>
                                            {item.quote && (
                                              <p className="talent-survey-theme-item-quote">“{item.quote}”</p>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })
                              )}

                              {/* {themesDetail?.reach_block && (
                                <section className="talent-survey-theme-reach">
                                  <h4>{themesDetail.reach_block.title || "How widely each theme is felt"}</h4>
                                  {themesDetail.reach_block.note && <p>{themesDetail.reach_block.note}</p>}
                                  {(themesDetail.reach_block.theme_keys || []).map((tKey) => {
                                    const item = themesMap[tKey];
                                    if (!item) return null;
                                    const reachPct = Number(item.reach_pct) || 0;
                                    const trackWidth = Math.min(100, Math.max(0, reachPct));
                                    return (
                                      <div className="talent-survey-theme-reach-row" key={tKey}>
                                        <span>{item.theme}</span>
                                        <div>
                                          <i style={{ width: `${trackWidth}%` }} />
                                        </div>
                                        <b>{item.respondents ? `${item.respondents} · ${reachPct}%` : `${item.mentions} · ${reachPct}%`}</b>
                                      </div>
                                    );
                                  })}
                                  {themesDetail.denominator_note && (
                                    <p className="talent-survey-theme-takeaway">• &nbsp;{themesDetail.denominator_note}</p>
                                  )}
                                </section>
                              )} */}
                            </div>
                          )
                        ) : <div className="talent-survey-theme-ask"><p>Ask a question about these themes</p><textarea aria-label="Ask about theme extraction" placeholder="e.g. What is driving negative feedback?" /><button type="button" disabled>Ask about these themes</button></div>}

                    <footer className="talent-survey-theme-panel-footer">
                      <button type="button" className="is-primary" disabled>Discuss in chat</button>
                      <button type="button" disabled>Add to readout</button>
                      <span>
                        {(() => {
                          const activeCard = cardsList.find(
                            (c) => c?.card === analysisPanelType || c?.name?.toLowerCase().includes(analysisPanelType)
                          );
                          const activePos = activeCard?.position !== undefined && activeCard?.position !== null
                            ? activeCard.position
                            : analysisPanelType === "ratings"
                              ? 3
                              : analysisPanelType === "sentiment"
                                ? 2
                                : 1;
                          const totalCards = cardsList.length || 3;
                          return `${activePos} of ${totalCards}`;
                        })()}
                      </span>
                    </footer>
                  </Modal>
                </div>
              ) : (
                <>
                  <h3 className="talent-survey-detail-section-title">SURVEY <span>— open for full analysis</span></h3>
                  <div className="talent-survey-wave-cards">
                    {(() => {
                      const primaryWave = selectedAnalysisSurvey.latest || selectedAnalysisSurvey.waves?.[0] || {};
                      const rawCreatedAt = primaryWave.created_at || selectedAnalysisSurvey.created_at || primaryWave.created_date || selectedAnalysisSurvey.created_date || primaryWave.creation_date || selectedAnalysisSurvey.creation_date;
                      const formattedCreatedAt = formatCreatedDate(rawCreatedAt);
                      const isWaveAnalyzing = isAnalyzing && (!analyzingWaveId || analyzingWaveId === primaryWave.id);
                      return (
                        <article
                          className={`talent-survey-wave-card${primaryWave.id === selectedAnalysisWaveId ? " is-selected" : ""}${isWaveAnalyzing ? " is-analyzing" : ""}`}
                          key={primaryWave.id || selectedAnalysisSurvey.id}
                          onClick={() => {
                            if (isCheckingStatus || isAnalyzing) return;
                            if (isAnalysisReady) {
                              setSelectedAnalysisWaveId(primaryWave.id);
                              setSelectedAnalysisWave(primaryWave);
                            } else {
                              handleRunAnalysis(primaryWave);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (isCheckingStatus || isAnalyzing) return;
                              if (isAnalysisReady) {
                                setSelectedAnalysisWaveId(primaryWave.id);
                                setSelectedAnalysisWave(primaryWave);
                              } else {
                                handleRunAnalysis(primaryWave);
                              }
                            }
                          }}
                        >
                          <div className="talent-survey-wave-card-top">
                            <strong>{selectedAnalysisSurvey.surveyTitle}</strong>
                            {primaryWave.windowDates && <time>{primaryWave.windowDates}</time>}
                          </div>
                          <div className="talent-survey-wave-card-score">
                            <strong>{primaryWave.responsesPct || "100%"}</strong>
                            <span>{primaryWave.responsesStr || ""}</span>
                          </div>
                          <div className={`talent-survey-wave-sentiment ${primaryWave.changeType || "positive"}`} aria-label={`${primaryWave.changeType || "positive"} movement`}>
                            <i /><i /><i /><i />
                          </div>
                          <div className="talent-survey-wave-card-meta">
                            <div className="talent-survey-wave-card-created">
                              <span className="talent-survey-wave-meta-label">Created at:</span>
                              <time className="talent-survey-wave-meta-value" title={rawCreatedAt || ""}>{formattedCreatedAt || "—"}</time>
                            </div>

                          </div>
                          {isWaveAnalyzing ? (
                            <div className="talent-survey-wave-card-analyzing" role="status" aria-live="polite">
                              <h4 className="talent-survey-analysing-title">Analysis scheduled</h4>
                              <p className="talent-survey-analysing-subtitle">Your analysis will be available here after the scheduled time.</p>
                              <div className="talent-survey-analysing-progress-track" aria-hidden="true">
                                <div className="talent-survey-analysing-progress-bar" />
                              </div>
                            </div>
                          ) : (
                            <div className="talent-survey-wave-card-actions">
                              <button
                                type="button"
                                aria-pressed={primaryWave.id === selectedAnalysisWaveId}
                                disabled={isCheckingStatus}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isCheckingStatus) return;
                                  if (isAnalysisReady) {
                                    setSelectedAnalysisWaveId(primaryWave.id);
                                    setSelectedAnalysisWave(primaryWave);
                                  } else {
                                    handleRunAnalysis(primaryWave);
                                  }
                                }}
                              >
                                {isCheckingStatus
                                  ? "Checking status..."
                                  : isAnalysisReady
                                    ? "Open analysis"
                                    : "Run analysis"}
                              </button>
                            </div>
                          )}
                        </article>
                      );
                    })()}
                  </div>
                </>
              )}
            </section>
          ) : (
            <div className="talent-survey-scheduled-container analysis-master-table-container">
              <MasterTable
                data={analysisRows}
                columns={analysisColumns}
                rowKey="id"
                className="scheduled-master-table analysis-master-table"
                pageSize={10}
                isLoading={analysisLoading}
                loadingMessage="Loading survey wave analysis..."
                emptyMessage={noDataText}
                onRowClick={(survey) => {
                  resetAnalysisState();
                  setSelectedAnalysisSurvey(survey);
                }}
                ariaLabel="Survey wave analysis"
              />
            </div>
          )
        )}

      </div>

      {/* ── MODAL 1: PREVIEW MODAL ── */}
      <Modal
        open={Boolean(previewSurvey)}
        onClose={() => setPreviewSurvey(null)}
        className="talent-survey-modal-container survey-preview-modal"
        footer={
          previewSurvey && (
            <div className="modal-footer-between modal-footer-end">
              <div className="modal-buttons-group">
                <button
                  type="button"
                  className="button-style primary-grey-button"
                  onClick={() => setPreviewSurvey(null)}
                >
                  {talentPulse360.CANCEL}
                </button>
                <button
                  type="button"
                  className="button-style primary-button"
                  onClick={() => {
                    const target = previewSurvey;
                    setPreviewSurvey(null);
                    handleOpenSendModal(target);
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )
        }
      >
        {previewSurvey && (
          <>
            {/* Modal Header */}
            <div className="talent-survey-modal-header preview-modal-header">
              <div>
                <h3 className="talent-survey-modal-title">{previewSurvey.title}</h3>
                <p className="talent-survey-modal-subtitle">These are the questions each person will see.</p>
              </div>
              <CloseButton onClick={() => setPreviewSurvey(null)} closeLabel={talentPulse360.CLOSE} />
            </div>

            {/* Metrics Bar */}
            <div className="preview-metrics-bar">
              <div className="preview-metric-col">
                <span className="preview-metric-label">QUESTIONS</span>
                <span className="preview-metric-val">{previewSurvey.questions ? previewSurvey.questions.length : previewSurvey.questionsCount || 5}</span>
              </div>
              <div className="preview-metric-col">
                <span className="preview-metric-label">TIME TO ANSWER</span>
                <span className="preview-metric-val">{previewSurvey.estimatedTime ? previewSurvey.estimatedTime.replace('to answer', '').trim() : "~2 min"}</span>
              </div>
              <div className="preview-metric-col">
                <span className="preview-metric-label">RESPONSES</span>
                <span className="preview-metric-val">Anonymous</span>
              </div>
              <div className="preview-metric-col">
                <span className="preview-metric-label">DELIVERED AS</span>
                <span className="preview-metric-val">Teams card</span>
              </div>
            </div>

            {/* Modal Body */}
            <div className="talent-survey-modal-body preview-modal-body">
              {/* Inner Card Box */}
              <div className="preview-teams-card-box">
                <div className="preview-card-box-header">
                  <h4 className="preview-card-box-title">{previewSurvey.title}</h4>
                  <span className="preview-card-box-sub">
                    As it appears in Teams · {previewSurvey.questions ? previewSurvey.questions.length : previewSurvey.questionsCount || 5} questions
                  </span>
                </div>

                {/* Questions List */}
                <div className="preview-questions-list">
                  {(previewSurvey.questions || []).map((q, idx) => {
                    const qType = normalizeQuestionType(q.type);
                    const qOptions = Array.isArray(q.options)
                      ? q.options
                      : Array.isArray(q.choices)
                        ? q.choices
                        : (q.options || "").split(",").map((opt) => opt.trim()).filter(Boolean);

                    return (
                      <div className="preview-question-item" key={q.id || idx}>
                        <div className="preview-question-header">
                          <span className="preview-question-num">{q.order || idx + 1}</span>
                          <span className="preview-question-text">{q.text}</span>
                        </div>

                        {qType === "short_text" ? (
                          <div className="preview-comment-box">
                            <textarea
                              className="preview-comment-textarea"
                              placeholder={`Up to ${q.maxLength || 120} characters`}
                              maxLength={q.maxLength || 120}
                              value={previewComments[q.id] || ""}
                              onChange={(e) => setPreviewComments({ ...previewComments, [q.id]: e.target.value })}
                            />
                          </div>
                        ) : qType === "single_choice" ? (
                          <div className="preview-choice-options-list">
                            {qOptions.map((opt, oIdx) => (
                              <label key={oIdx} className="preview-choice-option-item">
                                <input
                                  type="radio"
                                  name={`preview-choice-${q.id || idx}`}
                                  value={opt}
                                />
                                <span>{opt}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="preview-rating-scale-wrap">
                            <div className="preview-rating-boxes">
                              {[1, 2, 3, 4, 5].map((num) => (
                                <button
                                  key={num}
                                  type="button"
                                  className={`preview-rating-box ${previewRatings[q.id] === num ? "selected" : ""}`}
                                  onClick={() => setPreviewRatings({ ...previewRatings, [q.id]: num })}
                                >
                                  {num}
                                </button>
                              ))}
                            </div>
                            <div className="preview-rating-labels-sub">
                              {q.anchors?.low && q.anchors?.high
                                ? `${q.anchors.low} · ${q.anchors.high}`
                                : (idx === 0 ? "1 = not at all · 5 = highly motivated" :
                                  idx === 1 ? "1 = nowhere near · 5 = fully equipped" :
                                    idx === 2 ? "1 = unsustainable · 5 = comfortable" :
                                      idx === 3 ? "1 = never · 5 = always" :
                                        "1 = low · 5 = high")}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* ── MODAL 2: SEND SURVEY MODAL (PULSE TRIGGER) ── */}
      <Modal
        open={Boolean(sendModalSurvey)}
        onClose={() => setSendModalSurvey(null)}
        className="talent-survey-modal-container pulse-trigger-modal large"
        footer={
          sendModalSurvey && (
            <div className="modal-footer-between modal-footer-end">
              <div className="modal-buttons-group">
                <button
                  type="button"
                  className="button-style primary-grey-button"
                  onClick={() => setSendModalSurvey(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-style primary-button"
                  onClick={handleSendSurvey}
                  disabled={isSendingSurvey}
                >
                  {isSendingSurvey ? "Sending…" : talentPulse360.SEND}

                </button>
              </div>
            </div>
          )
        }
      >
        {sendModalSurvey && (
          <>
            <div className="talent-survey-modal-header">
              <div>
                <h3 className="talent-survey-modal-title">{sendModalSurvey.title}</h3>
                <p className="talent-survey-modal-subtitle">Choose who gets it and when it runs.</p>
              </div>
              <CloseButton onClick={() => setSendModalSurvey(null)} closeLabel={talentPulse360.CLOSE} />
            </div>

            {/* Body */}
            <div className="talent-survey-modal-body pulse-trigger-body">
              {/* Top Callout Card */}
              <div className="pulse-trigger-top-callout">
                <div className="pulse-trigger-callout-icon">
                  <span aria-hidden="true">✓</span>
                </div>
                <div className="pulse-trigger-callout-content">
                  <div className="pulse-trigger-callout-headline">
                    {sendModalSurvey.questions ? sendModalSurvey.questions.length : (sendModalSurvey.questionsCount || 5)} questions · about {sendModalSurvey.estimatedTime ? sendModalSurvey.estimatedTime.replace('~', '') : '2 minutes to answer'}
                  </div>
                  <div className="pulse-trigger-callout-sub">
                    Pre-built survey. The questions are fixed — you choose who gets it and when.
                  </div>
                </div>
              </div>

              {/* Send to Section */}
              <div className="pulse-trigger-form-section">
                <label className="pulse-trigger-label">Send to</label>
                <div className="pulse-trigger-sendto-box" ref={sendToBoxRef}>
                  <div className="pulse-trigger-tags-wrap">
                    {sendRecipients.map((recipient, idx) => (
                      <div className="pulse-trigger-tag-pill" key={recipient.id || idx}>
                        <span className="pulse-trigger-tag-badge">{recipient.type}</span>
                        <span className="pulse-trigger-tag-text">{recipient.text}</span>
                        <button
                          type="button"
                          className="pulse-trigger-tag-remove"
                          onClick={() => setSendRecipients(sendRecipients.filter((_, i) => i !== idx))}
                          aria-label={`Remove ${recipient.text}`}
                        >
                          <img src={closeIcon} alt="" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add Input / Dropdown */}
                  <div className="pulse-trigger-add-input-wrapper">
                    <input
                      type="search"
                      className="pulse-trigger-add-input-btn"
                      autoComplete="off"
                      placeholder="+ Add people, teams or a territory"
                      value={recipientSearch}
                      onFocus={() => setIsRecipientDropdownOpen(true)}
                      onClick={() => setIsRecipientDropdownOpen(true)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          setIsRecipientDropdownOpen(false);
                        }
                      }}
                      onChange={(event) => {
                        setRecipientSearch(event.target.value);
                        setIsRecipientDropdownOpen(true);
                      }}
                      aria-label="Search people to add"
                    />

                    {isRecipientDropdownOpen && (
                      <div className="pulse-trigger-recipient-dropdown">
                        <div className="pulse-trigger-dropdown-header">Select a person</div>
                        {isSearchingRecipients ? (
                          <div className="pulse-trigger-dropdown-item" aria-live="polite">Searching people…</div>
                        ) : recipientSearchResults.filter((opt) => {
                          const query = recipientSearch.trim().toLocaleLowerCase();
                          return !query || `${opt.name || ""} ${opt.email || ""}`.toLocaleLowerCase().includes(query);
                        }).length ? recipientSearchResults.filter((opt) => {
                          const query = recipientSearch.trim().toLocaleLowerCase();
                          return !query || `${opt.name || ""} ${opt.email || ""}`.toLocaleLowerCase().includes(query);
                        }).map((opt) => (
                          <div
                            key={opt.id}
                            className="pulse-trigger-dropdown-item"
                            onClick={() => {
                              if (!sendRecipients.some((recipient) => recipient.email === opt.email)) {
                                setSendRecipients((recipients) => [...recipients, opt]);
                              }
                              setIsRecipientDropdownOpen(false);
                              setRecipientSearch("");
                            }}
                          >
                            <span className="pulse-trigger-tag-badge">{opt.type}</span>
                            <span>{opt.name}{opt.email && opt.email !== opt.name ? ` · ${opt.email}` : ""}</span>
                          </div>
                        )) : (
                          <div className="pulse-trigger-dropdown-item">
                            {recipientSearch.trim() ? "No people found." : "Type a name or email to search."}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="pulse-trigger-subtext">
                  {totalRecipientsCount} people will receive a card in Teams, with an email fallback.
                </div>
              </div>

              {/* Timing Section */}
              <div className="pulse-trigger-dates-grid">
                <div className="pulse-trigger-date-col">
                  <label className="pulse-trigger-label">Start date &amp; time</label>
                  <div className="pulse-trigger-datetime-input-wrap">
                    <DateTimePicker
                      value={sendStartDateTime ? new Date(sendStartDateTime) : null}
                      minDate={new Date()}
                      onChange={(date) => {
                        if (!date) return;
                        const nextStart = formatDateTimeLocal(date);
                        setSendStartDateTime(nextStart);
                        const minimumEnd = getMinimumEndDateTime(nextStart);
                        if (!sendEndDateTime || new Date(sendEndDateTime) < minimumEnd) {
                          setSendEndDateTime(formatDateTimeLocal(minimumEnd));
                        }
                      }}
                      id="sendStartDateTime"
                    />
                  </div>
                </div>

                <div className="pulse-trigger-date-col">
                  <label className="pulse-trigger-label">End date &amp; time</label>
                  <div className="pulse-trigger-datetime-input-wrap">
                    <DateTimePicker
                      value={sendEndDateTime ? new Date(sendEndDateTime) : null}
                      minDate={getMinimumEndDateTime(sendStartDateTime)}
                      onChange={(date) => {
                        if (!date) {
                          setSendEndDateTime("");
                        } else {
                          setSendEndDateTime(formatDateTimeLocal(date));
                        }
                      }}
                      id="sendEndDateTime"
                    />
                  </div>
                </div>
              </div>

              <div className="pulse-trigger-dates-note">
                Cards are sent at the start time, and responses close at the end time. <strong>The end time must be at least 1 hour after the start time.</strong> All times are in IST.
              </div>

              {/* Bottom Callout Box */}
              <div className="pulse-trigger-bottom-callout">
                <div className="pulse-trigger-lightning-icon">
                  <span aria-hidden="true">⚡</span>
                </div>
                <div className="pulse-trigger-bottom-callout-content">
                  <div className="pulse-trigger-bottom-callout-title">What happens when you send</div>
                  <div className="pulse-trigger-bottom-callout-sub">
                    Power Automate builds a card for each person, sends it, chases anyone who hasn't replied, and writes every answer back to this page.
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* ── MODAL 3: CREATE / EDIT SURVEY MODAL ── */}
      <Modal
        open={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
        }}
        className="talent-survey-modal-container create-survey-modal"
      >
        <div className="talent-survey-modal-header create-modal-header">
          <div>
            <h3 className="talent-survey-modal-title">{editingSurveyId ? "Edit survey" : "Create a survey"}</h3>
            <p className="talent-survey-modal-subtitle">
              {editingSurveyId ? "Modify your survey name and questions." : "Give it a name and add your questions. It joins the catalogue and can be sent straight away."}
            </p>
          </div>
          <CloseButton onClick={() => setIsCreateModalOpen(false)} closeLabel={talentPulse360.CLOSE} />
        </div>

        <form onSubmit={handleSaveSurvey}>
          <div className="talent-survey-modal-body create-modal-body">

            {/* Survey Name */}
            <div className="create-survey-form-group">
              <label className="create-survey-label">Survey name</label>
              <input
                type="text"
                className="create-survey-input primary-focus"
                placeholder="e.g. Monsoon Workload Check"
                value={newSurveyTitle}
                onChange={(e) => setNewSurveyTitle(e.target.value)}
                required
              />
            </div>

            {/* What it's for */}
            <div className="create-survey-form-group">
              <label className="create-survey-label">What it's for</label>
              <input
                type="text"
                className="create-survey-input"
                placeholder="One line HR will see on the card"
                value={newSurveyDesc}
                onChange={(e) => setNewSurveyDesc(e.target.value)}
              />
            </div>

            {/* Questions Section */}
            <div className="create-survey-questions-section">
              <label className="create-survey-label">Questions</label>
              <div className="create-survey-questions-box">
                {newSurveyQuestions.map((q, idx) => (
                  <div key={q.id || idx} className="create-question-card">
                    <div className="create-question-row">
                      <span className="create-question-number">{idx + 1}</span>
                      <input
                        type="text"
                        className="create-survey-input question-text-input"
                        placeholder="Type your question"
                        value={q.text}
                        onChange={(e) => {
                          const updated = [...newSurveyQuestions];
                          updated[idx].text = e.target.value;
                          setNewSurveyQuestions(updated);
                        }}
                      />
                      <DropdownSelector
                        items={QUESTION_TYPE_OPTIONS}
                        selected={normalizeQuestionType(q.type)}
                        onChange={(val) => {
                          const updated = [...newSurveyQuestions];
                          updated[idx].type = val;
                          setNewSurveyQuestions(updated);
                        }}
                        itemConfig={{ getValue: (item) => item.id }}
                        showTriggerIcon={false}
                        placeholder={{ name: "Select type" }}
                      />

                      <button
                        type="button"
                        className="create-question-delete-btn"
                        onClick={() => {
                          if (newSurveyQuestions.length > 1) {
                            setNewSurveyQuestions(newSurveyQuestions.filter((_, i) => i !== idx));
                          }
                        }}
                        title="Delete question"
                        aria-label="Delete question"
                        disabled={newSurveyQuestions.length <= 1}
                      >
                        <img src={deleteIcon} alt="" aria-hidden="true" />
                      </button>
                    </div>

                    {/* Single choice options input */}
                    {normalizeQuestionType(q.type) === "single_choice" && (
                      <div className="single-choice-options-row">
                        <input
                          type="text"
                          className="create-survey-input choice-options-input"
                          placeholder="Options, separated by commas"
                          value={Array.isArray(q.options) ? q.options.join(", ") : (q.options || "")}
                          onChange={(e) => {
                            const updated = [...newSurveyQuestions];
                            updated[idx].options = e.target.value;
                            setNewSurveyQuestions(updated);
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add a question button */}
              <button
                type="button"
                className="add-question-dashed-btn"
                onClick={() => {
                  if (newSurveyQuestions.length < 5) {
                    setNewSurveyQuestions([
                      ...newSurveyQuestions,
                      { id: Date.now(), type: "scale_5", text: "" }
                    ]);
                  } else {
                    toast.info("Maximum 5 questions allowed per survey.");
                  }
                }}
              >
                + Add a question
              </button>
            </div>

            {/* Keep responses anonymous toggle */}
            <div className="anonymous-toggle-row">
              <div className="anonymous-toggle-info">
                <span className="anonymous-toggle-title">Keep responses anonymous</span>
                <span className="anonymous-toggle-sub">Names are removed before answers are stored.</span>
              </div>
              <label className="create-survey-switch">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                />
                <span className="create-survey-slider"></span>
              </label>
            </div>

          </div>

          {/* Footer */}
          <div className="talent-survey-modal-footer create-modal-footer modal-content-footer">
            <span className="create-modal-questions-count">
              {newSurveyQuestions.length} of 5 questions
            </span>
            <div className="create-modal-footer-btns">
              <button
                type="button"
                className="button-style primary-grey-button"
                onClick={() => {
                  setIsCreateModalOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button-style primary-button"
                disabled={isSavingSurvey}
              >
                {isSavingSurvey ? "Publishing…" : editingSurveyId ? "Save Changes" : "Save & publish"}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* ── MODAL 4: DELETE CONFIRMATION MODAL ── */}
      <Modal
        open={Boolean(deleteConfirmSurvey)}
        onClose={() => setDeleteConfirmSurvey(null)}
        className="delete-survey-modal"
      >
        {deleteConfirmSurvey && (
          <div className="modal-inner-content delete-modal-content">
            <h3 className="modal-title delete-modal-title">Delete Survey Form</h3>
            <p className="modal-message delete-modal-message">
              Are you sure you want to remove the <strong>"{deleteConfirmSurvey.title}"</strong> template? This will not affect previously completed surveys.
            </p>
            <div className="modal-buttons delete-modal-footer-btns">
              <button
                type="button"
                className="button-style primary-grey-button"
                onClick={() => setDeleteConfirmSurvey(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-style primary-button"
                onClick={() => handleDeleteSurvey(deleteConfirmSurvey.id)}
                disabled={isDeletingSurvey}
              >
                {isDeletingSurvey ? "Deleting…" : "Confirm"}
              </button>
            </div>
          </div>
        )}
      </Modal>
      {/* ── MODAL 5: EDIT SCHEDULED SEND MODAL ── */}
      <Modal
        open={Boolean(editScheduledSend)}
        onClose={() => setEditScheduledSend(null)}
        className="talent-survey-modal-container pulse-trigger-modal large"
        footer={
          editScheduledSend && (
            <div className="modal-footer-between modal-footer-end">
              <div className="modal-buttons-group">
                <button
                  type="button"
                  className="button-style primary-grey-button"
                  onClick={() => setEditScheduledSend(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-style primary-button"
                  onClick={handleSaveEditScheduledSend}
                >
                  Save
                </button>
              </div>
            </div>
          )
        }
      >
        {editScheduledSend && (
          <>
            <div className="talent-survey-modal-header">
              <div>
                <h3 className="talent-survey-modal-title">Edit {editScheduledSend.surveyTitle}</h3>
                <p className="talent-survey-modal-subtitle">Change who receives it and when it runs. The questions stay as they are.</p>
              </div>
              <CloseButton onClick={() => setEditScheduledSend(null)} closeLabel={talentPulse360.CLOSE} />
            </div>

            {/* Body */}
            <div className="talent-survey-modal-body pulse-trigger-body">
              {/* Callout Card */}
              <div className="edit-scheduled-callout">
                <div className="edit-scheduled-callout-text">
                  <strong>{talentPulse360.NO_CARDS_SENT}</strong> Changes apply to this send only and take effect immediately — the queued flow run is updated in place.
                </div>
              </div>

              {/* Send to Section */}
              <div className="pulse-trigger-form-section">
                <label className="pulse-trigger-label">Send to</label>
                <div className="pulse-trigger-sendto-box" ref={editSendToBoxRef}>
                  <div className="pulse-trigger-tags-wrap">
                    {editRecipients.map((recipient, idx) => (
                      <div className="pulse-trigger-tag-pill" key={recipient.id || idx}>
                        <span className="pulse-trigger-tag-badge">{recipient.type}</span>
                        <span className="pulse-trigger-tag-text">{recipient.text}</span>
                        <button
                          type="button"
                          className="pulse-trigger-tag-remove"
                          onClick={() => setEditRecipients(editRecipients.filter((_, i) => i !== idx))}
                          aria-label={`Remove ${recipient.text}`}
                        >
                          <img src={closeIcon} alt="" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="pulse-trigger-add-input-wrapper">
                    <button
                      type="button"
                      className="pulse-trigger-add-input-btn"
                      onClick={() => setIsEditRecipientDropdownOpen(!isEditRecipientDropdownOpen)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          setIsEditRecipientDropdownOpen(false);
                        }
                      }}
                    >
                      + Add people, teams or a territory
                    </button>

                    {isEditRecipientDropdownOpen && (
                      <div className="pulse-trigger-recipient-dropdown">
                        <div className="pulse-trigger-dropdown-header">Select recipient or group</div>
                        {AVAILABLE_RECIPIENT_OPTIONS.map((opt) => (
                          <div
                            key={opt.id}
                            className="pulse-trigger-dropdown-item"
                            onClick={() => {
                              if (!editRecipients.some(r => r.text === opt.text)) {
                                setEditRecipients([...editRecipients, opt]);
                              }
                              setIsEditRecipientDropdownOpen(false);
                            }}
                          >
                            <span className="pulse-trigger-tag-badge">{opt.type}</span>
                            <span>{opt.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="pulse-trigger-subtext">
                  {editRecipients.reduce((acc, item) => acc + (item.count || 1), 0)} people will receive a card in Teams, with an email fallback.
                </div>
              </div>

              {/* Timing Section */}
              <div className="pulse-trigger-dates-grid">
                <div className="pulse-trigger-date-col">
                  <label className="pulse-trigger-label">Start date &amp; time</label>
                  <div className="pulse-trigger-datetime-input-wrap">
                    <DateTimePicker
                      value={editStartDateTime ? new Date(editStartDateTime) : null}
                      onChange={(date) => {
                        if (!date) {
                          setEditStartDateTime("");
                        } else {
                          const nextStart = formatDateTimeLocal(date);
                          setEditStartDateTime(nextStart);
                          const minimumEnd = getMinimumEndDateTime(nextStart);
                          if (!editEndDateTime || new Date(editEndDateTime) < minimumEnd) {
                            setEditEndDateTime(formatDateTimeLocal(minimumEnd));
                          }
                        }
                      }}
                      id="editStartDateTime"
                    />
                  </div>
                </div>

                <div className="pulse-trigger-date-col">
                  <label className="pulse-trigger-label">End date &amp; time</label>
                  <div className="pulse-trigger-datetime-input-wrap">
                    <DateTimePicker
                      value={editEndDateTime ? new Date(editEndDateTime) : null}
                      minDate={getMinimumEndDateTime(editStartDateTime)}
                      onChange={(date) => {
                        if (!date) {
                          setEditEndDateTime("");
                        } else {
                          setEditEndDateTime(formatDateTimeLocal(date));
                        }
                      }}
                      id="editEndDateTime"
                    />
                  </div>
                </div>
              </div>

              <div className="pulse-trigger-dates-note edit-scheduled-dates-note">
                Cards are sent at the start time, and responses close at the end time. <strong>The end time must be at least 1 hour after the start time.</strong> All times are in IST.
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* ── MODAL 6: CANCEL SCHEDULED SEND CONFIRMATION ── */}
      <Modal
        open={Boolean(cancelConfirmItem)}
        onClose={() => setCancelConfirmItem(null)}
        className="talent-survey-modal-container cancel-confirm-modal"
        footer={
          cancelConfirmItem && (
            <div className="modal-footer-between">
              <div className="pulse-trigger-flow-status">
                <span className="pulse-trigger-green-dot">●</span>
                <span className="pulse-trigger-status-text">The flow run will be stopped</span>
              </div>
              <div className="modal-buttons-group">
                <button
                  type="button"
                  className="button-style primary-grey-button"
                  onClick={() => setCancelConfirmItem(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-style primary-button"
                  onClick={handleConfirmCancelScheduledSend}
                >
                  Confirm
                </button>
              </div>
            </div>
          )
        }
      >
        {cancelConfirmItem && (
          <>
            <div className="talent-survey-modal-header">
              <div>
                <h3 className="talent-survey-modal-title">Cancel this scheduled send?</h3>
                <p className="talent-survey-modal-subtitle">No cards have gone out yet.</p>
              </div>
              <CloseButton onClick={() => setCancelConfirmItem(null)} closeLabel={talentPulse360.CLOSE} />
            </div>

            <div className="talent-survey-modal-body cancel-confirm-body">
              {/* Warning callout */}
              <div className="cancel-confirm-callout">
                <span className="cancel-confirm-callout-icon" aria-hidden="true">!</span>
                <div>
                  <div className="cancel-confirm-callout-title">
                    {cancelConfirmItem.surveyTitle} to {cancelConfirmItem.recipientCount}
                  </div>
                  <div className="cancel-confirm-callout-text">
                    Due to open {cancelConfirmItem.opensDate} for {cancelConfirmItem.recipientGroup}. Nobody has received a card, so there is nothing to recall.
                  </div>
                </div>
              </div>

              {/* Reason input */}
              <div className="create-survey-form-group">
                <label className="create-survey-label">
                  Why is it being cancelled? <span className="label-optional">{talentPulse360.OPTIONAL}</span>
                </label>
                <input
                  type="text"
                  className="create-survey-input"
                  placeholder="e.g. postponed until after the sales conference"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
                <p className="cancel-confirm-hint">
                  Recorded against the dispatch so the audit trail explains the gap.
                </p>
              </div>
            </div>
          </>
        )}
      </Modal>

    </div>
  );
};

export default TalentPulse360Survey;
